import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import ReactDOM from 'react-dom';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Clock, Video, VideoOff, Users, Target, Trophy, Calendar, Inbox, SearchX, ChevronDown, ChevronUp, ChevronRight, User, Briefcase, Search, Activity, FileText, SlidersHorizontal, X, AlertCircle, CheckCircle2, Building2, MapPin, ArrowRight, ArrowLeft, ExternalLink, Sparkles, Star, UserCheck, MessageSquare, Mail } from 'lucide-react';
import PortfolioClient from '../data/api/PortfolioClient';
import { getResumePreviewUrl, getResumeDownloadUrl } from '../../../services/resumeFileService';
import ResumeViewer, { ResumeZoomControls, ResumeViewerHandle } from '../../../shared/components/ResumeViewer';
import Loader from '../../../shared/components/Loader';
import Modal from '../../../shared/components/Modal';
import Alert from '../../../shared/components/Alert';
import ResultStatsPage from '../../interviewResults/pages/ResultStatsPage';
import AdminFeedbackPage from '../../interviewResults/pages/AdminFeedbackPage';
import { CandidateDetailModal } from '../../jobSearch/pages/JobSearchResultPage';
import PortfolioEditModal from '../components/PortfolioEditModal';
import JobOpportunityClient, { JobOpportunity } from '../../jobOpportunity/data/api/JobOpportunityClient';
import JobApplicationClient from '../../jobApplications/data/api/JobApplicationClient';
import { useAuthContext } from '../../auth/data/context/AuthContext';
import Select from 'react-select';
import '../styles/PortfolioStyles.css';

const selectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: '40px',
    height: '40px',
    border: state.isFocused ? '1.5px solid var(--primary)' : '1.5px solid var(--border)',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.85rem',
    fontFamily: 'inherit',
    color: 'var(--text-heading)',
    background: 'var(--surface)',
    boxShadow: state.isFocused ? '0 0 0 3px var(--primary-ghost)' : 'none',
    '&:hover': {
      borderColor: state.isFocused ? 'var(--primary)' : 'var(--border)'
    },
    outline: 'none',
    transition: 'border-color var(--transition), box-shadow var(--transition)',
  }),
  valueContainer: (base: any) => ({ ...base, padding: '0 12px' }),
  singleValue: (base: any, state: any) => ({ ...base, color: state.isDisabled ? 'var(--text-muted)' : 'var(--text-heading)' }),
  input: (base: any) => ({
    ...base,
    color: 'var(--text-heading)',
    margin: '0',
    padding: '0',
    border: 'none',
    outline: 'none',
    boxShadow: 'none',
    minHeight: 'auto',
    height: 'auto',
    'input:focus': {
      boxShadow: 'none',
      border: 'none',
      outline: 'none'
    }
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base: any) => ({ ...base, padding: '4px' }),
  menu: (base: any) => ({
    ...base,
    zIndex: 9999,
    fontFamily: 'inherit',
    fontSize: '0.85rem',
  }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  option: (base: any, { isFocused, isSelected }: any) => ({
    ...base,
    backgroundColor: isSelected ? 'var(--primary)' : isFocused ? 'var(--primary-light)' : 'var(--surface)',
    color: isSelected ? '#fff' : 'var(--text-heading)',
    cursor: 'pointer',
    ':active': {
      backgroundColor: 'var(--primary)',
      color: '#fff'
    }
  })
};

const EmptyJobIllustration = () => (
  <svg width="240" height="160" viewBox="0 0 240 160" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: '20px' }}>
    <path d="M150 50 C150 40, 160 30, 175 30 C185 30, 195 35, 200 45 C210 45, 215 55, 210 65 C205 70, 195 75, 180 75 C160 75, 150 65, 150 50 Z" fill="#FDF8F0" />
    <path d="M40 70 C40 65, 45 60, 55 60 C60 60, 65 62, 70 67 C75 67, 80 72, 75 77 C72 80, 65 82, 55 82 C45 82, 40 77, 40 70 Z" fill="#FDF8F0" />
    <path d="M60 40 L62 45 L67 47 L62 49 L60 54 L58 49 L53 47 L58 45 Z" fill="#FDE6B8" />
    <path d="M180 90 L181.5 94.5 L186 96 L181.5 97.5 L180 102 L178.5 97.5 L174 96 L178.5 94.5 Z" fill="#FDE6B8" />
    <path d="M70 110 L71 113 L74 114 L71 115 L70 118 L69 115 L66 114 L69 113 Z" fill="#FDE6B8" />

    <ellipse cx="120" cy="80" rx="80" ry="30" stroke="#E6D3B3" strokeWidth="1" strokeDasharray="4 4" fill="none" transform="rotate(-10 120 80)" />

    <rect x="90" y="35" width="80" height="100" rx="6" fill="#FFF8F0" transform="rotate(10 130 85)" />
    <rect x="85" y="40" width="80" height="100" rx="6" fill="#FFFFFF" stroke="#F1E5D1" strokeWidth="2" transform="rotate(5 125 90)" />

    <rect x="95" y="75" width="55" height="6" rx="3" fill="#F8F3EA" transform="rotate(5 125 90)" />
    <rect x="95" y="90" width="60" height="6" rx="3" fill="#F8F3EA" transform="rotate(5 125 90)" />
    <rect x="95" y="105" width="45" height="6" rx="3" fill="#F8F3EA" transform="rotate(5 125 90)" />

    <g transform="translate(130, 48) rotate(5)">
      <rect x="0" y="0" width="28" height="28" rx="6" fill="#FDF8F0" />
      <path d="M7 12 h14 v8 a3 3 0 0 1 -3 3 h-8 a3 3 0 0 1 -3 -3 z" fill="#D4B07B" />
      <path d="M10 12 v-2 a2 2 0 0 1 2 -2 h4 a2 2 0 0 1 2 2 v2" fill="none" stroke="#D4B07B" strokeWidth="2" />
      <rect x="12" y="14" width="4" height="3" rx="1" fill="#FFF" />
    </g>

    <g transform="translate(70, 70) rotate(-15)" style={{ animation: 'float-icon 3s ease-in-out infinite' }}>
      <circle cx="24" cy="24" r="20" fill="none" stroke="#C49B5B" strokeWidth="6" />
      <circle cx="24" cy="24" r="17" fill="#FFFFFF" fillOpacity="0.4" />
      <rect x="36" y="36" width="24" height="8" rx="4" fill="#A87F42" transform="rotate(45 36 36)" />
      <path d="M14 14 A 14 14 0 0 1 34 14" fill="none" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" />
    </g>
  </svg>
);

const EmptyInterviewIllustration = () => (
  <svg width="240" height="160" viewBox="0 0 240 160" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: '20px' }}>
    <path d="M150 50 C150 40, 160 30, 175 30 C185 30, 195 35, 200 45 C210 45, 215 55, 210 65 C205 70, 195 75, 180 75 C160 75, 150 65, 150 50 Z" fill="#F0FDF4" />
    <path d="M40 70 C40 65, 45 60, 55 60 C60 60, 65 62, 70 67 C75 67, 80 72, 75 77 C72 80, 65 82, 55 82 C45 82, 40 77, 40 70 Z" fill="#F0FDF4" />
    <path d="M60 40 L62 45 L67 47 L62 49 L60 54 L58 49 L53 47 L58 45 Z" fill="#BBE2C6" />
    <path d="M180 90 L181.5 94.5 L186 96 L181.5 97.5 L180 102 L178.5 97.5 L174 96 L178.5 94.5 Z" fill="#BBE2C6" />
    <path d="M70 110 L71 113 L74 114 L71 115 L70 118 L69 115 L66 114 L69 113 Z" fill="#BBE2C6" />

    <ellipse cx="120" cy="80" rx="80" ry="30" stroke="#C2E0CC" strokeWidth="1" strokeDasharray="4 4" fill="none" transform="rotate(-10 120 80)" />

    <rect x="90" y="35" width="80" height="100" rx="6" fill="#F4FBF6" transform="rotate(10 130 85)" />
    <rect x="85" y="40" width="80" height="100" rx="6" fill="#FFFFFF" stroke="#D1E8DA" strokeWidth="2" transform="rotate(5 125 90)" />

    <rect x="95" y="75" width="55" height="6" rx="3" fill="#EAF3EE" transform="rotate(5 125 90)" />
    <rect x="95" y="90" width="60" height="6" rx="3" fill="#EAF3EE" transform="rotate(5 125 90)" />
    <rect x="95" y="105" width="45" height="6" rx="3" fill="#EAF3EE" transform="rotate(5 125 90)" />

    <g transform="translate(130, 48) rotate(5)">
      <rect x="0" y="0" width="28" height="28" rx="6" fill="#F0FDF4" />
      <path d="M16 11 L20 8 L20 20 L16 17 L16 20 A 2 2 0 0 1 14 22 L6 22 A 2 2 0 0 1 4 20 L4 8 A 2 2 0 0 1 6 6 L14 6 A 2 2 0 0 1 16 8 Z" fill="#4ADE80" />
    </g>

    <g transform="translate(70, 70) rotate(-15)" style={{ animation: 'float-icon 3s ease-in-out infinite' }}>
      <circle cx="24" cy="24" r="20" fill="none" stroke="#22C55E" strokeWidth="6" />
      <circle cx="24" cy="24" r="17" fill="#FFFFFF" fillOpacity="0.4" />
      <rect x="36" y="36" width="24" height="8" rx="4" fill="#166534" transform="rotate(45 36 36)" />
      <path d="M14 14 A 14 14 0 0 1 34 14" fill="none" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" />
    </g>
  </svg>
);

const RingScore = ({ score, size = 52, stroke = 5, color, status }: { score: number | string | null | undefined, size?: number, stroke?: number, color: string, status?: string }) => {
  const r = (size / 2) - stroke;
  const c = 2 * Math.PI * r;

  const numScore = Number(score);
  const isValid = score !== null && score !== undefined && score !== 'None' && !isNaN(numScore);

  const off = isValid ? c - (numScore / 100) * c : c;
  const finalColor = isValid ? color : '#94a3b8';

  let displayScore: React.ReactNode = '—';
  if (isValid) {
    displayScore = numScore;
  } else if (score === 'None' || score === null || score === undefined) {
    const s = (status || '').toLowerCase();
    if (s === 'missed') displayScore = <span style={{ fontSize: '10px' }}>Missed</span>;
    else if (s === 'upcoming') displayScore = <span style={{ fontSize: '10px' }}>Upc</span>;
    else if (s === 'initiated' || s === 'in progress' || s === 'active') displayScore = <span style={{ fontSize: '10px' }}>TBD</span>;
    else displayScore = '—';
  }

  return (
    <div className="ring-score" style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} stroke="var(--rule)" strokeWidth={stroke} fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} stroke={finalColor} strokeWidth={stroke} fill="none" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} />
      </svg>
      <div className="rn" style={{ fontSize: isValid ? undefined : '13px', color: isValid ? undefined : '#64748b' }}>{displayScore}</div>
    </div>
  );
};

const MatchRing = ({
  score,
  size = 56,
  showCenterText = false,
}: {
  score: number;
  size?: number;
  showCenterText?: boolean;
}) => {
  const color = score >= 85 ? "#059669" : score >= 65 ? "#d97706" : "#dc2626";
  const trackColor = score >= 85 ? "#d1fae5" : score >= 65 ? "#fef3c7" : "#fee2e2";
  const iconSize = Math.max(10, Math.round(size * 0.38));

  return (
    <div
      className="match-ring"
      style={{
        width: size,
        height: size,
        background: `conic-gradient(${color} ${score * 3.6}deg, ${trackColor} 0deg)`,
      }}
    >
      <div className="mn">
        {showCenterText ? (
          <>
            <b>{score}</b>
            <span>MATCH</span>
          </>
        ) : (
          <Sparkles size={iconSize} color={color} />
        )}
      </div>
    </div>
  );
};

const badgeClass = (status: string) => {
  const s = (status || '').toLowerCase();
  if (s === "scored") return "badge-scored";
  if (s === "completed") return "badge-completed";
  if (s === "active" || s === "initiated") return "badge-initiated";
  if (s === "upcoming") return "badge-upcoming";
  if (s === "cancelled") return "badge-cancelled";
  if (s === "missed" || s === "failed") return "badge-failed";
  return "badge-default";
};

const verdictClass = (v: string) => {
  if (v === "Strong Hire") return "verdict-strong";
  if (v === "Hire") return "verdict-hire";
  if (v === "No Hire" || v === "Reject") return "verdict-no";
  return "verdict-default";
};

const getVCardClass = (status: string) => {
  const s = (status || '').toLowerCase();
  if (s === 'upcoming') return 'vcard-upcoming';
  if (s === 'missed') return 'vcard-missed';
  if (s === 'cancelled') return 'vcard-cancelled';
  if (s === 'initiated' || s === 'in progress' || s === 'active') return 'vcard-initiated';
  if (s === 'completed') return 'vcard-completed';
  if (s === 'scored') return 'vcard-scored';
  return '';
};

const Field = ({ label, val }: { label: string, val: any }) => {
  const empty = !val || val === "—" || val === "";
  return (
    <div className="field">
      <label>{label}</label>
      <div
        className={`val ${empty ? 'empty' : ''}`}
        onMouseEnter={(e) => {
          if (empty) return;
          const el = e.currentTarget;
          if (el.scrollWidth > el.clientWidth) {
            el.setAttribute('data-title', typeof val === 'string' ? val : '');
          } else {
            el.removeAttribute('data-title');
          }
        }}
      >
        {empty ? <span style={{ color: 'var(--ink-faint)', fontStyle: 'italic', fontWeight: 400 }}>Not on file</span> : val}
      </div>
    </div>
  );
};

const FrostedGlassStatCard = ({
  icon,
  heading,
  count,
  accent = 'default',
  onClick,
  isActive,
}: {
  icon: React.ReactNode;
  heading: string;
  count: React.ReactNode;
  accent?: string;
  onClick?: () => void;
  isActive?: boolean;
}) => {
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    const handleMouseMove = (e: MouseEvent) => {
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const cx = rect.width / 2;
      const cy = rect.height / 2;
      card.style.transform = `perspective(1000px) rotateX(${((y - cy) / cy) * -6}deg) rotateY(${((x - cx) / cx) * 6}deg) scale3d(1.02,1.02,1.02)`;
      card.style.setProperty('--mouse-x', `${x}px`);
      card.style.setProperty('--mouse-y', `${y}px`);
    };
    const handleMouseLeave = () => {
      card.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1,1,1)';
    };
    card.addEventListener('mousemove', handleMouseMove as any);
    card.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      card.removeEventListener('mousemove', handleMouseMove as any);
      card.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  const clickable = typeof onClick === 'function';

  return (
    <div className={`glass-card-container accent-${accent}${clickable ? ' glass-clickable' : ''}${isActive ? ' is-active' : ''}`}>
      <div
        ref={cardRef}
        className="glass-card"
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        onClick={onClick}
        onKeyDown={
          clickable
            ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
            : undefined
        }
        title={clickable ? `Filter by ${heading.toLowerCase()}` : undefined}
      >
        <div className="glass-row" style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div className="glass-icon-wrapper">{icon}</div>
            <h2 className="glass-heading">{heading.toUpperCase()}</h2>
          </div>
          <div className="glass-count-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <h3 className="glass-count">{count}</h3>
            {clickable && <ChevronRight size={16} className="glass-go" />}
          </div>
        </div>
        <div className="glass-glare" />
      </div>
    </div>
  );
};

const PortfolioDetailPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const id = searchParams.get('candidate_id');
  const actionParam = searchParams.get('action');
  const navigate = useNavigate();
  const location = useLocation();

  const handleClose = () => {
    navigate('/dashboard/portfolio', { state: location.state });
  };

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editScope, setEditScope] = useState<'all' | 'personal' | 'docs' | 'financials'>('all');
  const [alertInfo, setAlertInfo] = useState<{ show: boolean; title: string; message: string; type: 'success' | 'error' } | null>(null);
  const [activeTab, setActiveTab] = useState<'profile' | 'interviews' | 'jobs' | 'applications' | 'docs'>('profile');
  const [activeEventTab, setActiveEventTab] = useState<'completed' | 'active' | 'upcoming' | 'missed'>('completed');
  const [tabDirection, setTabDirection] = useState<1 | -1>(1);
  const [isTabLoading, setIsTabLoading] = useState(false);

  const [appSearch, setAppSearch] = useState('');
  const [appFilter, setAppFilter] = useState<'all' | 'active' | 'offered' | 'rejected'>('all');
  const [appSort, setAppSort] = useState<'newest' | 'oldest' | 'title'>('newest');
  const [candidateApplications, setCandidateApplications] = useState<any[]>([]);
  const [appsLoading, setAppsLoading] = useState(false);
  const [selectedApp, setSelectedApp] = useState<any | null>(null);
  const [timelineData, setTimelineData] = useState<any | null>(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [detailViewTab, setDetailViewTab] = useState<'application' | 'job'>('application');
  const [expandedNotes, setExpandedNotes] = useState<Set<number>>(new Set());

  const toggleNote = (idx: number) => {
    setExpandedNotes(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const [deleteModal, setDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteConfirm = async () => {
    if (!data?.profile?.email_address) return;
    setDeleting(true);
    try {
      const LearnerClient = (await import('../../learnerControl/data/api/LearnerClient')).default;
      await LearnerClient.delete(data.profile.email_address);
      setDeleteModal(false);
      handleClose();
    } catch (err) {
      console.error(err);
      setAlertInfo({ show: true, title: 'Error', message: 'Failed to delete applicant.', type: 'error' });
    } finally {
      setDeleting(false);
    }
  };
  const tabOrder = ['profile', 'interviews', 'jobs', 'applications', 'docs'] as const;
  const { state: authState } = useAuthContext();

  useEffect(() => {
    if (data?.portfolio) {
      const events = [...(data.portfolio.interviews || []), ...(data.portfolio.assessments || [])];
      const completedCount = events.filter(i => {
        const s = (i.status || '').toLowerCase();
        return s !== 'upcoming' && s !== 'active' && s !== 'initiated' && s !== 'in progress' && s !== 'missed' && s !== 'cancelled';
      }).length;
      const activeCount = events.filter(i => {
        const s = (i.status || '').toLowerCase();
        return s === 'active' || s === 'initiated' || s === 'in progress';
      }).length;
      const upcomingCount = events.filter(i => (i.status || '').toLowerCase() === 'upcoming').length;
      const missedCount = events.filter(i => {
        const s = (i.status || '').toLowerCase();
        return s === 'missed' || s === 'cancelled';
      }).length;

      const counts = { completed: completedCount, active: activeCount, upcoming: upcomingCount, missed: missedCount };
      if (counts[activeEventTab] === 0) {
        if (completedCount > 0) setActiveEventTab('completed');
        else if (activeCount > 0) setActiveEventTab('active');
        else if (upcomingCount > 0) setActiveEventTab('upcoming');
        else if (missedCount > 0) setActiveEventTab('missed');
      }
    }
  }, [data, activeEventTab]);

  useEffect(() => {
    if (actionParam === 'edit' && data?.profile) {
      setIsEditModalOpen(true);
      // Remove action param to prevent reopening on reload
      searchParams.delete('action');
      setSearchParams(searchParams, { replace: true });
    }
  }, [actionParam, data?.profile, searchParams, setSearchParams]);

  // Assigned To inline edit
  const [adminUsers, setAdminUsers] = useState<{ email: string; name: string; role?: string }[]>([]);
  const [assignedToEditing, setAssignedToEditing] = useState(false);
  const [assignedToSaving, setAssignedToSaving] = useState(false);
  const [assignedToValue, setAssignedToValue] = useState<string>('');
  const [recruiterSearch, setRecruiterSearch] = useState('');

  const saveAssignedTo = async () => {
    setAssignedToSaving(true);
    try {
      const profile = data?.profile;
      if (!profile) throw new Error("No profile");
      const LearnerClient = (await import('../../learnerControl/data/api/LearnerClient')).default;
      await LearnerClient.update(profile.email_address, { assigned_to: assignedToValue || null });
      setData((prev: any) => ({ ...prev, profile: { ...prev.profile, assigned_to: assignedToValue } }));
      setAssignedToEditing(false);
      setAlertInfo({ show: true, title: 'Saved', message: 'Assignment updated successfully.', type: 'success' });
    } catch {
      setAlertInfo({ show: true, title: 'Error', message: 'Failed to update assignment.', type: 'error' });
    } finally {
      setAssignedToSaving(false);
    }
  };
  const loadCandidateApplications = useCallback(async (candidateId: string) => {
    if (!candidateId) return;
    setAppsLoading(true);
    try {
      const res = await JobApplicationClient.getCandidateApplications(candidateId);
      const apps = res.records || (Array.isArray(res) ? res : []);
      setCandidateApplications(apps);
    } catch (err) {
      console.error('Failed to load candidate applications:', err);
    } finally {
      setAppsLoading(false);
    }
  }, []);

  const handleTabChange = (newTab: typeof activeTab) => {
    if (newTab === activeTab) return;
    const oldIdx = tabOrder.indexOf(activeTab);
    const newIdx = tabOrder.indexOf(newTab);
    setTabDirection(newIdx > oldIdx ? 1 : -1);
    setActiveTab(newTab);
    setSelectedApp(null);
    setTimelineData(null);
    setDetailedFeedbackSessionId(null);
    setIsTabLoading(true);
    if (newTab === 'applications' && id) {
      loadCandidateApplications(id);
    }
    setTimeout(() => setIsTabLoading(false), 450);
  };

  const [drawerHtml, setDrawerHtml] = useState<{ __html: string } | null>(null);
  const [drawerContent, setDrawerContent] = useState<React.ReactNode | null>(null);
  const [detailedFeedbackSessionId, setDetailedFeedbackSessionId] = useState<string | null>(null);
  const [selectedJobIndex, setSelectedJobIndex] = useState<number | null>(null);
  const [auditTrailDrawerOpen, setAuditTrailDrawerOpen] = useState(false);
  const [auditFilter, setAuditFilter] = useState('All');

  // Document Preview Modal State
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [resumePreviewUrl, setResumePreviewUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [resumeZoom, setResumeZoom] = useState(1.5);
  const [previewTitle, setPreviewTitle] = useState('Document Preview');
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const resumeViewerRef = useRef<ResumeViewerHandle>(null);

  const closePreview = () => {
    setIsPreviewModalOpen(false);
    setResumePreviewUrl(null);
  };

  const loadData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [res, appsRes] = await Promise.allSettled([
        PortfolioClient.getPortfolio(id),
        JobApplicationClient.getCandidateApplications(id),
      ]);
      if (res.status === 'fulfilled') {
        setData(res.value);
        setAssignedToValue(res.value?.profile?.assigned_to || '');
      }
      if (appsRes.status === 'fulfilled') {
        const apps = appsRes.value?.records || (Array.isArray(appsRes.value) ? appsRes.value : []);
        setCandidateApplications(apps);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Load admin users for the assigned_to dropdown
  useEffect(() => {
    PortfolioClient.getAdminUsers()
      .then((res: any) => {
        const users: { email: string; name: string; role?: string }[] = [];
        // Super admin or current user record (always active if returned)
        if (res?.you && res.you.status === 'Active') {
          users.push({ email: res.you.email_address, name: res.you.full_name || res.you.email_address, role: res.you.role_name || res.you.role || 'Super Admin' });
        }
        // Sub-admin records (filtered by Active status)
        (res?.others || res?.records || []).forEach((u: any) => {
          if (u.status === 'Active' && !users.find((x: any) => x.email === u.email_address)) {
            users.push({ email: u.email_address, name: u.full_name || u.email_address, role: u.role_name || u.role || 'Admin' });
          }
        });
        setAdminUsers(users);
      })
      .catch(() => setAdminUsers([]));
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openDrawer = (html: string) => { setDrawerHtml({ __html: html }); setDrawerContent(null); }
  const closeDrawer = () => { setDrawerHtml(null); setDrawerContent(null); }

  if (loading) {
    return (
      <div className="portfolio-detail-wrapper">
        <div className="stage">
          <div className="dossier-wrap">
            <div className="folder-tabs">
              <button className="ftab t-profile active">Profile</button>
              <button className="ftab t-interviews" style={{ opacity: 0.5, pointerEvents: 'none' }}>Interviews</button>
              <button className="ftab t-jobs" style={{ opacity: 0.5, pointerEvents: 'none' }}>Job Matches</button>
              <button className="ftab t-applications" style={{ opacity: 0.5, pointerEvents: 'none' }}>Job Applications</button>
              <button className="ftab t-docs" style={{ opacity: 0.5, pointerEvents: 'none' }}>Documents</button>
            </div>
            <div className="dossier">
              <div className="dossier-page">
                <div className="tab-shimmer" style={{ padding: '44px 46px', display: 'flex', flexDirection: 'column', gap: '24px', height: '100%' }}>
                  <div className="shimmer-line" style={{ width: '30%', height: 32, borderRadius: 8, marginBottom: 16 }} />
                  <div style={{ display: 'flex', gap: 24 }}>
                    <div className="shimmer-line" style={{ width: 80, height: 80, borderRadius: '50%' }} />
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 12, justifyContent: 'center' }}>
                      <div className="shimmer-line" style={{ width: '40%', height: 24, borderRadius: 6 }} />
                      <div className="shimmer-line" style={{ width: '60%', height: 16, borderRadius: 6 }} />
                    </div>
                  </div>
                  <div className="shimmer-line" style={{ width: '100%', height: 120, borderRadius: 12, marginTop: 20 }} />
                  <div className="shimmer-line" style={{ width: '100%', height: 120, borderRadius: 12 }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!data || !data.profile) {
    return (
      <div className="portfolio-detail-wrapper" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div>
          <p>Candidate not found or portfolio is empty.</p>
          <button onClick={handleClose} style={{ padding: '8px 16px' }}>Back to Directory</button>
        </div>
      </div>
    );
  }

  const { profile, interviews = [], assessments = [], job_searches = [], audit_trail = [], job_applications = [] } = data;
  const init1 = profile.first_name ? profile.first_name[0].toUpperCase() : '';
  const init2 = profile.last_name ? profile.last_name[0].toUpperCase() : '';
  const fullName = `${profile.first_name || ''} ${profile.last_name || ''}`.trim();

  // Merge and sort events
  const allEvents = [
    ...(interviews || []).map((i: any) => ({ ...i, eventType: 'interview' })),
    ...(assessments || []).map((a: any) => ({ ...a, eventType: 'assessment' }))
  ];
  const sortedEvents = allEvents.sort((a, b) =>
    new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
  );

  const showFeedback = (sessionId: string) => {
    const iv = sortedEvents.find(i => i.session_id === sessionId && i.eventType === 'interview');
    if (!iv) return;

    setDrawerHtml(null);
    setDrawerContent(
      <ResultStatsPage
        inlineSessionId={sessionId}
        inlineCandidateId={profile.candidate_id}
        inlineReturnPath={`/dashboard/portfolio/detail?candidate_id=${encodeURIComponent(id || '')}`}
        isInline
        onDetailedClick={() => {
          closeDrawer();
          setDetailedFeedbackSessionId(sessionId);
        }}
        onClose={closeDrawer}
      />
    );
  };

  const showJob = (idx: number) => {
    const j = job_searches[idx];
    if (!j) return;

    if (j.candidate_result) {
      setDrawerHtml(null);
      setDrawerContent(
        <CandidateDetailModal
          candidate={j.candidate_result}
          index={idx}
          total={job_searches.length}
          onNavigate={showJob}
          onClose={closeDrawer}
          isInline
          jobDescription={j.job_description}
        />
      );
      return;
    }

    const html = `
      <h3>${j.title || 'Job Match'}</h3><div class="drawer-sub">searched ${j.search_date} by ${j.searched_by || 'System'}</div>
      <div class="dsec"><div style="display:flex;align-items:center;gap:16px;">
        <div class="match-ring" style="width:64px;height:64px;background:conic-gradient(${j.match_score >= 85 ? '#3f8a5c' : j.match_score >= 65 ? '#c17c2b' : '#b23b3b'} ${j.match_score * 3.6}deg, #e4d9c0 0deg)">
          <div class="mn"><b>${j.match_score || 0}</b><span>MATCH</span></div>
        </div>
        <div><div style="font-family:'Fraunces',serif;font-size:15px;font-weight:600;">${j.status || 'New'}</div>
        </div></div>
        <div class="score-tiles" style="margin-top:16px;">
          <div class="score-tile"><b>${j.skills_score || 0}</b><span>Skills</span></div>
          <div class="score-tile"><b>${j.experience_score || 0}</b><span>Experience</span></div>
          <div class="score-tile"><b>${j.education_score || 0}</b><span>Education</span></div></div></div>
      <div class="dsec"><h4>Job Description</h4><p>${j.job_description || 'No description'}</p></div>
    `;
    openDrawer(html);
  };

  const getStageBadgeClass = (stage: string) => {
    const s = (stage || '').toLowerCase();
    if (s === 'applied') return 'applied';
    if (s === 'shortlisted' || s === 'screening') return 'shortlisted';
    if (s === 'interviewed' || s === 'interview') return 'interviewed';
    if (s === 'offered' || s === 'offer') return 'offered';
    if (s === 'accepted' || s === 'hired') return 'accepted';
    if (s === 'rejected' || s === 'failed') return 'rejected';
    if (s === 'declined') return 'declined';
    return 'default';
  };

  const viewAppDetails = async (app: any) => {
    setSelectedApp(app);
    setDetailViewTab('application');
    setExpandedNotes(new Set());
    setTimelineLoading(true);
    setTimelineData(null);
    try {
      const res = await JobApplicationClient.getApplicationTimeline(app.opportunity_id, app.candidate_id || profile.candidate_id);
      setTimelineData(res);
    } catch (e) {
      console.error(e);
      setTimelineData({ error: 'Failed to load application timeline.' });
    } finally {
      setTimelineLoading(false);
    }
  };

  const showApplicationTimeline = viewAppDetails;

  const showDocument = async (title: string, url: string | null = null) => {
    setPreviewTitle(title);
    setIsPreviewModalOpen(true);
    setResumePreviewUrl(null);
    setPdfLoading(true);

    try {
      const docUrl = url || (data?.profile?.resume_url);
      if (docUrl) {
        const inlineUrl = await getResumePreviewUrl(docUrl);
        setResumePreviewUrl(inlineUrl);
      } else {
        setResumePreviewUrl(null); // No document available
      }
    } catch (e) {
      console.error(e);
      setResumePreviewUrl(null);
    } finally {
      setPdfLoading(false);
    }
  };

  const renderResumeSummary = (resumeData: any) => {
    if (!resumeData) return null;
    const { education = [], professional_experience = [], projects = [] } = resumeData;
    if (education.length === 0 && professional_experience.length === 0 && projects.length === 0) return null;

    return (
      <div style={{ marginTop: '40px' }}>
        <div className="section-label">Resume Summary</div>
        <div style={{ background: 'var(--paper-2)', borderRadius: '12px', padding: '32px', border: '1px solid var(--rule)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px' }}>
            <div>
              {/* EDUCATION */}
              {education.length > 0 && (
                <>
                  <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.05em', color: 'var(--ink-soft)', textTransform: 'uppercase', marginBottom: '16px' }}>Education</div>
                  <div className="rs-timeline">
                    {education.map((ed: any, i: number) => (
                      <div key={i} className="rs-item">
                        <div className="rs-item-title">{ed.degree}</div>
                        <div className="rs-item-subtitle">{ed.institution} {ed.score ? `· ${ed.score}` : ''}</div>
                        <div className="rs-item-date">{ed.startDate || ''} {ed.startDate && ed.endDate ? '→' : ''} {ed.endDate || ''}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div>
              {/* PROFESSIONAL EXPERIENCE */}
              {professional_experience.length > 0 && (
                <>
                  <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.05em', color: 'var(--ink-soft)', textTransform: 'uppercase', marginBottom: '16px' }}>Professional Experience</div>
                  <div className="rs-timeline">
                    {professional_experience.map((pe: any, i: number) => (
                      <div key={i} className="rs-item">
                        <div className="rs-item-title">{pe.role}</div>
                        <div className="rs-item-subtitle">{pe.organization} {pe.type ? `· ${pe.type}` : ''}</div>
                        <div className="rs-item-date">{pe.startDate || ''} {pe.startDate && pe.endDate ? '→' : ''} {pe.endDate || ''}</div>
                        {pe.responsibilities && pe.responsibilities.length > 0 && (
                          <div className="rs-item-desc">{pe.responsibilities[0]}</div>
                        )}
                        {pe.impact_metrics && pe.impact_metrics.length > 0 && (
                          <div className="rs-metrics">
                            {pe.impact_metrics.map((m: string, j: number) => <span key={j} className="rs-metric">{m}</span>)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* PROJECTS */}
              {projects && projects.length > 0 && (
                <>
                  <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.05em', color: 'var(--ink-soft)', textTransform: 'uppercase', marginBottom: '16px', marginTop: '32px' }}>Projects</div>
                  <div className="rs-timeline">
                    {projects.map((pr: any, i: number) => (
                      <div key={i} className="rs-item">
                        <div className="rs-item-title">{pr.title || pr.name}</div>
                        <div className="rs-item-desc">{pr.description}</div>
                        {pr.skills && (
                          <div className="rs-tags">
                            {pr.skills.map((s: string, j: number) => <span key={j} className="rs-tag">{s}</span>)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderProfileTab = () => (
    <>
      <div className="top-actions">
        <button
          className="close-btn edit-btn"
          onClick={() => { setEditScope('all'); setIsEditModalOpen(true); }}
        >
          <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
          Edit Portfolio
        </button>
        <button
          className="close-btn"
          onClick={() => setDeleteModal(true)}
          style={{ color: '#ef4444', borderColor: '#ef4444', background: 'transparent', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
        >
          <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
          Delete Applicant
        </button>
        <button
          className="close-btn apply-btn"
          onClick={() => setIsApplyModalOpen(true)}
          style={{ background: 'var(--primary)', color: 'white', borderColor: 'var(--primary)', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
        >
          <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>
          Apply to Job
        </button>
        <button className="close-btn" onClick={handleClose}>✕ Close File</button>
      </div>
      <div className="page-pad">
        <div className="page-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div><div className="eyebrow">Section 01 · Candidate Record</div><h1>Applicant Profile</h1></div>
        </div>
        <div className="profile-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 50 }}>
          <div style={{ display: 'flex', gap: '22px', alignItems: 'center' }}>
            <div className="avatar">{init1}{init2}</div>
            <div className="who">
              <h2 style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {fullName}
              </h2>
              <div className="sub">
                <span className={`badge ${profile.status === 'Active' ? 'badge-active' : ''}`}>{profile.status ? profile.status.toUpperCase() : 'ACTIVE'}</span>
                <span>{(profile.email_address && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(profile.email_address)) ? profile.email_address : "No email on file"}</span><span>{profile.phone || profile.phone_number || 'No phone on file'}</span>
              </div>

              {(() => {
                const missingFields = [(!profile.first_name || profile.first_name === 'Unknown') ? 'First Name' : null, (!profile.last_name || profile.last_name === 'Unknown') ? 'Last Name' : null, (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(profile.email_address || '')) ? 'Email' : null].filter(Boolean);
                if (missingFields.length > 0) {
                  return (
                    <div style={{ marginTop: '12px', background: 'rgba(239, 68, 68, 0.05)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '8px', padding: '10px 14px', display: 'flex', alignItems: 'flex-start', gap: '10px', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}>
                      <AlertCircle size={18} color="#dc2626" style={{ marginTop: '2px', flexShrink: 0 }} />
                      <div>
                        <div style={{ color: '#b91c1c', fontWeight: 600, fontSize: '14px', marginBottom: '4px' }}>Action Required</div>
                        <div style={{ color: '#991b1b', fontSize: '13px', lineHeight: '1.4' }}>
                          Missing fields: {missingFields.join(', ')}. Edit to fix.
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              {/* ── Assigned Recruiter Glassy Card ── */}
              <div style={{ marginTop: '14px', background: 'linear-gradient(135deg, rgba(240,253,244,0.85) 0%, rgba(255,255,255,0.6) 100%)', border: '1px solid rgba(187,247,208,0.9)', borderRadius: '14px', padding: '10px 14px', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', boxShadow: '0 4px 20px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.8)' }}>
                {(() => {
                  const assignedUser = adminUsers.find((u: any) => u.email === assignedToValue);
                  const initials = assignedUser
                    ? assignedUser.name.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2)
                    : assignedToValue
                      ? assignedToValue.substring(0, 2).toUpperCase()
                      : '??';
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', position: 'relative', zIndex: 50 }}>
                      {/* Recruiter avatar + info */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(240,253,244,0.7)', border: '1px solid rgba(187,247,208,0.8)', borderRadius: '30px', padding: '4px 12px 4px 4px', backdropFilter: 'blur(6px)' }}>
                        <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'linear-gradient(135deg, #059669 0%, #0f766e 100%)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 700, flexShrink: 0 }}>
                          {initials}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                          <span style={{ fontSize: '9px', color: '#059669', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Assigned Recruiter</span>
                          <span style={{ fontSize: '12px', fontWeight: 700, color: '#0f172a' }}>
                            {assignedUser ? assignedUser.name : (assignedToValue || 'Not Assigned')}
                          </span>
                        </div>
                        {assignedToValue && (
                          <span style={{ background: '#0f766e', color: 'white', fontSize: '9px', fontWeight: 700, padding: '2px 7px', borderRadius: '10px', letterSpacing: '0.03em' }}>{assignedUser?.role || 'Recruiter'}</span>
                        )}
                      </div>

                      {/* Assign / Change button */}
                      <div className="shiny-btn-container" onClick={() => { setAssignedToEditing(true); }}>
                        <button className="shiny-btn-inner">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" /><path d="M5 3v4" /><path d="M19 17v4" /><path d="M3 5h4" /><path d="M17 19h4" /></svg>
                          {assignedToValue ? 'Change' : 'Assign'}
                        </button>
                      </div>

                      {/* Assign to me button — hidden if already assigned to me */}
                      {assignedToValue !== authState?.user?.email_address && (
                        <div className="shiny-btn-container yellow" onClick={async () => {
                          const me = authState?.user?.email_address;
                          if (me && !assignedToSaving) {
                            setAssignedToSaving(true);
                            try {
                              const profileData = data?.profile;
                              if (!profileData) throw new Error('No profile');
                              const LearnerClient = (await import('../../learnerControl/data/api/LearnerClient')).default;
                              await LearnerClient.update(profileData.email_address, { assigned_to: me });
                              setAssignedToValue(me);
                              setData((prev: any) => ({ ...prev, profile: { ...prev.profile, assigned_to: me } }));
                              setAlertInfo({ show: true, title: 'Saved', message: 'Assigned to you successfully.', type: 'success' });
                            } catch {
                              setAlertInfo({ show: true, title: 'Error', message: 'Failed to update assignment.', type: 'error' });
                            } finally {
                              setAssignedToSaving(false);
                            }
                          }
                        }}>
                          <button className="shiny-btn-inner" disabled={assignedToSaving} style={{ cursor: assignedToSaving ? 'wait' : 'pointer' }}>
                            Assign to me
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="7" y1="17" x2="17" y2="7"></line><polyline points="7 7 17 7 17 17"></polyline></svg>
                          </button>
                        </div>
                      )}

                      {/* Edit mode: absolute dropdown anchored to the row */}
                      {assignedToEditing && (
                        <>
                          {/* Invisible backdrop to dismiss */}
                          <div
                            style={{ position: 'fixed', inset: 0, zIndex: 1000 }}
                            onClick={() => { setAssignedToEditing(false); setRecruiterSearch(''); }}
                          />

                          {/* Dropdown panel */}
                          <div style={{
                            position: 'absolute',
                            top: 'calc(100% + 8px)',
                            left: 0,
                            zIndex: 1001,
                            width: '360px',
                            background: '#ffffff',
                            border: '1px solid #e2e8f0',
                            borderRadius: '18px',
                            boxShadow: '0 8px 16px rgba(0,0,0,0.08), 0 32px 80px rgba(0,0,0,0.20)',
                            overflow: 'hidden',
                          }}>
                            {/* Search bar */}
                            <div style={{
                              padding: '12px 14px',
                              borderBottom: '1px solid #f1f5f9',
                              display: 'flex', alignItems: 'center', gap: '8px',
                              background: '#fafafa',
                            }}>
                              <svg width="14" height="14" fill="none" stroke="#94a3b8" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35" /></svg>
                              <input
                                autoFocus
                                placeholder="Search by name or email…"
                                value={recruiterSearch}
                                onChange={e => setRecruiterSearch(e.target.value)}
                                style={{
                                  border: 'none', outline: 'none',
                                  fontSize: '13px', color: '#0f172a',
                                  background: 'transparent', width: '100%',
                                }}
                              />
                              {recruiterSearch && (
                                <button
                                  onClick={() => setRecruiterSearch('')}
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0, lineHeight: 1 }}
                                >✕</button>
                              )}
                            </div>

                            {/* Section label */}
                            <div style={{ padding: '10px 16px 4px', fontSize: '9px', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                              Available Recruiters
                            </div>

                            {/* List */}
                            <div style={{ maxHeight: '240px', overflowY: 'auto', padding: '0 8px 8px' }}>
                              {adminUsers
                                .filter((u: any) => !recruiterSearch || u.name.toLowerCase().includes(recruiterSearch.toLowerCase()) || u.email.toLowerCase().includes(recruiterSearch.toLowerCase()))
                                .map((u: any, idx: number) => {
                                  const avatarColors = ['#0f766e', '#6366f1', '#db2777', '#ea580c', '#7c3aed', '#0284c7'];
                                  const color = avatarColors[idx % avatarColors.length];
                                  const uInitials = u.name.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
                                  const isSelected = assignedToValue === u.email;
                                  return (
                                    <div
                                      key={u.email}
                                      onClick={async () => {
                                        setAssignedToValue(u.email);
                                        setAssignedToSaving(true);
                                        setAssignedToEditing(false);
                                        setRecruiterSearch('');
                                        try {
                                          const profileData = data?.profile;
                                          if (!profileData) throw new Error('No profile');
                                          const LearnerClient = (await import('../../learnerControl/data/api/LearnerClient')).default;
                                          await LearnerClient.update(profileData.email_address, { assigned_to: u.email });
                                          setData((prev: any) => ({ ...prev, profile: { ...prev.profile, assigned_to: u.email } }));
                                          setAlertInfo({ show: true, title: 'Saved', message: `Assigned to ${u.name} successfully.`, type: 'success' });
                                        } catch {
                                          setAlertInfo({ show: true, title: 'Error', message: 'Failed to update assignment.', type: 'error' });
                                        } finally {
                                          setAssignedToSaving(false);
                                        }
                                      }}
                                      style={{
                                        display: 'flex', alignItems: 'center', gap: '10px',
                                        padding: '8px 10px', borderRadius: '10px', marginBottom: '2px',
                                        cursor: 'pointer',
                                        background: isSelected ? 'rgba(15,118,110,0.08)' : 'transparent',
                                        border: isSelected ? '1px solid rgba(15,118,110,0.2)' : '1px solid transparent',
                                        transition: 'all 0.15s',
                                      }}
                                      onMouseEnter={e => { if (!isSelected) { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.border = '1px solid #e2e8f0'; } }}
                                      onMouseLeave={e => { if (!isSelected) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.border = '1px solid transparent'; } }}
                                    >
                                      <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: color, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700, flexShrink: 0 }}>
                                        {uInitials}
                                      </div>
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                          <span style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>{u.name}</span>
                                          <span style={{ background: '#f0fdf4', color: '#059669', fontSize: '9px', fontWeight: 700, padding: '1px 6px', borderRadius: '8px', border: '1px solid #bbf7d0', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{u.role || 'Recruiter'}</span>
                                        </div>
                                        <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>
                                      </div>
                                      {isSelected
                                        ? <svg width="14" height="14" fill="none" stroke="#0f766e" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                                        : <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#22c55e', flexShrink: 0 }} />}
                                    </div>
                                  );
                                })
                              }
                            </div>

                            {/* Footer */}
                            <div style={{ padding: '10px 16px 14px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end' }}>
                              <button
                                onClick={() => { setAssignedToEditing(false); setRecruiterSearch(''); setAssignedToValue(data?.profile?.assigned_to || ''); }}
                                style={{
                                  fontSize: '12px', color: '#64748b', fontWeight: 500,
                                  background: '#f8fafc', border: '1px solid #e2e8f0',
                                  borderRadius: '8px', cursor: 'pointer', padding: '6px 16px',
                                  transition: 'all 0.15s',
                                }}
                                onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = '#f8fafc'; }}
                              >Cancel</button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })()}
              </div> {/* end glassy card */}
            </div> {/* end .who */}
          </div> {/* end outer flex */}
        </div> {/* end .profile-head */}

        <div className="section-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Personal Information
          <button
            onClick={() => { setEditScope('personal'); setIsEditModalOpen(true); }}
            style={{ fontSize: '12px', color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
          >Edit</button>
        </div>
        <div className="field-grid">
          <Field label="Gender" val={profile.gender} />
          <Field label="Date of Birth" val={profile.date_of_birth} />
          <Field label="Country" val={profile.country} />
          <Field label="State" val={profile.state} />
          <Field label="Source" val={profile.source} />
          <Field label="Education" val={profile.education} />
        </div>

        <div className="section-label" style={{ marginTop: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Professional Details
          <button
            onClick={() => { setEditScope('personal'); setIsEditModalOpen(true); }}
            style={{ fontSize: '12px', color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
          >Edit</button>
        </div>
        <div className="field-grid">
          <Field label="Experience" val={profile.year_of_experience || profile.years_of_experience ? `${profile.year_of_experience || profile.years_of_experience} yrs` : null} />
          <Field label="Work Preference" val={profile.work_preference} />
          <Field label="Expected CTC" val={profile.expected_ctc} />
        </div>

        <div style={{ marginTop: '24px' }}>
          <label style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '10px', textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--ink-faint)', display: 'block', marginBottom: '8px' }}>Resume Document</label>
          {profile.resume_url ? (
            <div
              style={{ display: 'inline-flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.02)', transition: 'all 0.2s' }}
              onClick={() => showDocument('Resume', profile.resume_url)}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#94a3b8'; e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.05)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.02)'; }}
            >
              <div style={{ width: '40px', height: '40px', background: '#f1f5f9', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>{profile.first_name}_Resume.pdf</span>
                <span style={{ fontSize: '12px', color: '#64748b' }}>Click to view document</span>
              </div>
            </div>
          ) : (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: '12px' }}>
              <span style={{ fontSize: '13px', color: '#94a3b8', fontStyle: 'italic' }}>No resume document uploaded</span>
            </div>
          )}
        </div>

        <div style={{ marginTop: '30px' }}>
          <label style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '10px', textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--ink-faint)', display: 'block', marginBottom: '8px' }}>Top 10 Skills</label>
          <div className="pill-row">
            {(profile.skills && profile.skills.length > 0) ? (
              profile.skills.map((s: string, i: number) => <span key={i} className="pill pill-yellow">{s}</span>)
            ) : (
              <span style={{ fontSize: '12px', color: 'var(--ink-faint)', fontStyle: 'italic' }}>No skills extracted yet</span>
            )}
          </div>
        </div>

        {profile.job_categories?.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            <label style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '10px', textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--ink-faint)', display: 'block', marginBottom: '8px' }}>Job Categories</label>
            <div className="pill-row">
              {profile.job_categories?.map((s: string, i: number) => <span key={`job-${i}`} className="pill pill-green">{s}</span>)}
            </div>
          </div>
        )}

        {profile.professional_certifications?.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            <label style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '10px', textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--ink-faint)', display: 'block', marginBottom: '8px' }}>Professional Certifications</label>
            <div className="pill-row">
              {profile.professional_certifications?.map((s: string, i: number) => <span key={`cert-${i}`} className="pill pill-blue">{s}</span>)}
            </div>
          </div>
        )}

        {renderResumeSummary(profile.resume_data)}

        {/* ===== Admin Note + Audit Trail two-column row ===== */}
        <div className="admin-audit-row">

          {/* ── Left: Admin Note ── */}
          <div className="admin-note-col">
            <div className="section-label">Admin Note</div>
            <div className="corkboard">
              <div className="postit"><span className="pin"></span>{profile.admin_notes || profile.note || <span style={{ color: 'rgba(0,0,0,0.4)', fontStyle: 'italic' }}>No administrative notes on file for this candidate.</span>}<div className="postit-tag">— System</div></div>
            </div>
          </div>

          {/* ── Right: Audit Trail ── */}
          <div className="audit-trail-col">
            <div className="section-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Audit Trail</span>
              <span className="audit-trail-count" style={{ marginTop: '-4px' }}>{audit_trail.length} events</span>
            </div>
            <AuditTrailPanel
              entries={audit_trail}
              onShowAll={() => setAuditTrailDrawerOpen(true)}
            />
          </div>
        </div>
      </div>

      {/* Audit Trail Drawer / Modal */}
      {(() => {
        const filteredAuditTrail = auditFilter === 'All' ? audit_trail : audit_trail.filter((e: any) => {
          if (auditFilter === 'event') return e.category === 'interview' || e.category === 'assessment';
          return e.category === auditFilter;
        });

        const customAuditTitle = (
          <div className="audit-modal-header-wrapper">
            <div className="audit-modal-header">
              <div className="audit-modal-header-left">
                <div className="audit-modal-icon">
                  <Activity size={20} strokeWidth={2.5} />
                </div>
                <div className="audit-modal-title-group">
                  <div className="audit-modal-title-row">
                    <h3>Timeline & Audit Trail</h3>
                    <span className="audit-modal-count-pill">{filteredAuditTrail.length} total events</span>
                  </div>
                  <p className="audit-modal-subtitle">Complete chronological tamper-evident record of actions, transitions, and system events.</p>
                </div>
              </div>
            </div>

            <div className="audit-modal-filters-row">
              <div className="audit-filter-segment">
                {[{ key: 'All', label: 'All Actions' }, { key: 'profile', label: 'Profile' }, { key: 'event', label: 'Events' }, { key: 'job_search', label: 'Search' }, { key: 'job_application', label: 'Application' }].map(tab => (
                  <button
                    key={tab.key}
                    className={`audit-filter-btn ${auditFilter === tab.key ? 'active' : ''}`}
                    onClick={() => setAuditFilter(tab.key)}
                  >
                    {tab.label}
                    {auditFilter === tab.key && tab.key === 'All' && <span className="audit-filter-badge">{audit_trail.length}</span>}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

        return (
          <Modal
            open={auditTrailDrawerOpen}
            onClose={() => setAuditTrailDrawerOpen(false)}
            title={customAuditTitle}
            width="600px"
            className="audit-drawer-modal"
          >
            <div className="audit-trail-modal-content">
              <AuditTrailPanel entries={filteredAuditTrail} isDrawer={true} />
            </div>
          </Modal>
        );
      })()}
    </>
  );

  const renderEventsTab = () => {
    if (detailedFeedbackSessionId) {
      return (
        <div style={{ position: 'absolute', inset: 0, overflow: 'auto', background: '#f8fafc', borderRadius: '12px', zIndex: 10, padding: '24px 32px' }}>
          <AdminFeedbackPage
            inlineSessionId={detailedFeedbackSessionId}
            inlineCandidateId={profile.candidate_id}
            isInline
            onClose={() => setDetailedFeedbackSessionId(null)}
          />
        </div>
      );
    }

    const total = sortedEvents.length;
    const validScores = sortedEvents.map(i => Number(i.score)).filter(s => !isNaN(s) && s !== null);
    const avg = validScores.length ? Math.round(validScores.reduce((a, b) => a + b, 0) / validScores.length) : 'N/A';
    const max = validScores.length ? Math.max(...validScores) : 'N/A';

    let last = '—';
    if (total > 0 && sortedEvents[0].created_at && sortedEvents[0].created_at !== 'None') {
      const ts = sortedEvents[0].created_at;
      try {
        const d = new Date(ts);
        if (!isNaN(d.getTime())) last = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        else last = ts.split('T')[0].split(' ')[0];
      } catch (e) {
        last = ts.split('T')[0];
      }
    }

    const formatTime = (ts: string) => {
      if (!ts || ts === "None") return "—";
      try {
        const d = new Date(ts);
        if (isNaN(d.getTime())) return ts.split('.')[0].replace('T', ' ');
        return d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
      } catch (e) {
        return ts.split('.')[0].replace('T', ' ');
      }
    };

    const upcoming = sortedEvents.filter(i => (i.status || '').toLowerCase() === 'upcoming');
    const active = sortedEvents.filter(i => {
      const s = (i.status || '').toLowerCase();
      return s === 'active' || s === 'initiated' || s === 'in progress';
    });
    const missed = sortedEvents.filter(i => {
      const s = (i.status || '').toLowerCase();
      return s === 'missed' || s === 'cancelled';
    });
    const completed = sortedEvents.filter(i => {
      const s = (i.status || '').toLowerCase();
      return s !== 'upcoming' && s !== 'active' && s !== 'initiated' && s !== 'in progress' && s !== 'missed';
    });

    const renderEventCard = (iv: any, idx: number, isActionable: boolean = true) => {
      const isScoredInterview = iv.eventType === 'interview' && iv.session_id && iv.session_id !== 'None' && (iv.status || '').toLowerCase() === 'scored';
      const isScoredAssessment = iv.eventType === 'assessment' && iv.assessment_id && iv.assessment_id !== 'None' && (iv.status || '').toLowerCase() === 'scored';

      let displayScore = iv.score;
      if (iv.score != null && iv.score !== 'None') {
        const numScore = Number(iv.score);
        if (!isNaN(numScore)) {
          const rawScore = iv.eventType === 'interview' ? numScore * 10 : numScore;
          displayScore = Math.round(rawScore);
        }
      }
      const numDisplayScore = Number(displayScore);
      const scoreColor = isNaN(numDisplayScore) ? "#64748b" : (numDisplayScore >= 80 ? "#3f8a5c" : numDisplayScore >= 60 ? "#c17c2b" : "#b23b3b");

      return (
        <div className="vitem" key={idx}>
          <div className={`vcard ${getVCardClass(iv.status)}`} onClick={() => isActionable && isScoredInterview ? showFeedback(iv.session_id) : null} style={{ cursor: (isActionable && isScoredInterview) ? 'pointer' : 'default' }}>
            <RingScore score={displayScore} status={iv.status} color={scoreColor} />
            <div className="vmid">
              <div className="vtop">
                <span className="sname">{iv.session_name || iv.assessment_name || (iv.eventType === 'assessment' ? 'Assessment' : 'Interview Round')}</span>
                {iv.eventType === 'assessment' ? (
                  <span className="badge round-tech">ASSESSMENT</span>
                ) : (
                  <span className={`badge ${iv.round_type === 'TECHNICAL' ? 'round-tech' : 'round-hr'}`}>INTERVIEW{iv.round_type ? ` · ${iv.round_type}` : ''}</span>
                )}
                {activeEventTab === 'completed' && (
                  <span className={`badge ${badgeClass(iv.status)}`}>{iv.status || 'Unknown'}</span>
                )}
              </div>
              <div className="ts">
                {iv.status && (iv.status.toLowerCase() === 'upcoming' || iv.status.toLowerCase() === 'active') ? (
                  (!iv.start_datetime || iv.start_datetime === 'None')
                    ? <>Not scheduled yet</>
                    : <>Scheduled for: {formatTime(iv.start_datetime)}</>
                ) : (
                  formatTime(iv.created_at)
                )}
              </div>
              {iv.eventType === 'assessment' && (iv.tag_jozuna?.length || iv.tag_programming?.length || iv.tag_module_names?.length) && (
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
                  {iv.tag_jozuna?.map((t: string) => <span key={`jz-${t}`} className="badge" style={{ backgroundColor: '#f1f5f9', color: '#475569', fontSize: '10px' }}>{t}</span>)}
                  {iv.tag_programming?.map((t: string) => <span key={`pr-${t}`} className="badge" style={{ backgroundColor: '#f0fdf4', color: '#166534', fontSize: '10px' }}>{t}</span>)}
                  {iv.tag_module_names?.map((t: string) => <span key={`md-${t}`} className="badge" style={{ backgroundColor: '#eff6ff', color: '#1e40af', fontSize: '10px' }}>{t}</span>)}
                </div>
              )}
              <div className="vfoot" style={{ display: 'flex', alignItems: 'center', gap: '16px', color: '#64748b', fontSize: '13px', marginTop: '8px' }}>
                {iv.status && (iv.status.toLowerCase() === 'upcoming' || iv.status.toLowerCase() === 'active' || iv.status.toLowerCase() === 'missed' || iv.status.toLowerCase() === 'cancelled') ? (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Calendar size={14} />
                      <span>
                        {(() => {
                          if (!iv.start_datetime || !iv.end_datetime || iv.start_datetime === 'None' || iv.end_datetime === 'None') return 'Pending time';
                          try {
                            const s = new Date(iv.start_datetime);
                            const e = new Date(iv.end_datetime);
                            if (isNaN(s.getTime()) || isNaN(e.getTime())) return 'Pending time';
                            return `${s.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} - ${e.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
                          } catch (e) { return 'Pending time'; }
                        })()}
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Clock size={14} />
                      <span>{iv.duration && iv.duration !== 'None' ? iv.duration : 'Not recorded'}</span>
                    </div>
                    {(iv.eventType !== 'assessment' && iv.status && iv.status.toLowerCase() !== 'upcoming' && iv.status.toLowerCase() !== 'active' && iv.status.toLowerCase() !== 'missed' && iv.status.toLowerCase() !== 'cancelled' && iv.video_recording && iv.video_recording !== 'None') ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Video size={14} color="#059669" />
                        <span>Recorded</span>
                      </div>
                    ) : (iv.eventType !== 'assessment' && iv.status && iv.status.toLowerCase() !== 'upcoming' && iv.status.toLowerCase() !== 'active' && iv.status.toLowerCase() !== 'missed' && iv.status.toLowerCase() !== 'cancelled') ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <VideoOff size={14} opacity={0.6} />
                        <span>No recording</span>
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            </div>
            {(iv.verdict && iv.verdict !== 'None') && (
              <span className={`verdict-chip ${verdictClass(iv.verdict)}`} style={{ padding: '4px 12px', fontSize: '12px', marginLeft: 'auto', fontWeight: 600 }}>
                {iv.verdict}
              </span>
            )}
            {(isActionable && (isScoredInterview || isScoredAssessment)) && <button className="vbtn" onClick={(e) => {
              e.stopPropagation();
              if (isScoredInterview) {
                showFeedback(iv.session_id);
              } else if (isScoredAssessment) {
                navigate(`/dashboard/assessments/${iv.assessment_id}/results/${id}`);
              }
            }} style={{ marginLeft: (iv.verdict && iv.verdict !== 'None') ? '0' : 'auto' }}>View Feedback</button>}
          </div>
        </div>
      );
    };

    return (
      <>
        <button className="close-btn" onClick={handleClose}>✕ Close File</button>
        <div className="page-pad">
          <div className="page-head">
            <div><div className="eyebrow">Section 02 · Session Log</div><h1>Event History</h1></div>
            <div className="head-meta">Candidate<br /><b style={{ color: 'var(--ink)' }}>{fullName}</b></div>
          </div>
          <div className="summary-strip">
            <div className="stat-chip">
              <div className="stat-icon"><Users size={20} /></div>
              <div className="stat-content">
                <div className="n">{total}</div>
                <div className="l">Total Events</div>
              </div>
            </div>
            <div className="stat-chip">
              <div className="stat-icon"><Target size={20} /></div>
              <div className="stat-content">
                <div className="n">{avg}</div>
                <div className="l">Average Score</div>
              </div>
            </div>
            <div className="stat-chip">
              <div className="stat-icon"><Trophy size={20} /></div>
              <div className="stat-content">
                <div className="n">{max}</div>
                <div className="l">Highest Score</div>
              </div>
            </div>
            <div className="stat-chip">
              <div className="stat-icon"><Calendar size={20} /></div>
              <div className="stat-content">
                <div className="n" style={{ fontSize: '15px' }}>{last}</div>
                <div className="l">Last Event</div>
              </div>
            </div>
          </div>
          {sortedEvents.length === 0 && (
            <div className="empty-state">
              <EmptyInterviewIllustration />
              <h3>No Events Yet</h3>
              <p>This candidate has not participated in any events. Schedule an interview or assessment to get started.</p>
            </div>
          )}

          {sortedEvents.length > 0 && (
            <div className="event-tabs" style={{ display: 'flex', gap: '12px', marginBottom: '24px', borderBottom: '1px solid var(--rule)' }}>
              {completed.length > 0 && <button className={`etab ${activeEventTab === 'completed' ? 'active' : ''}`} onClick={() => setActiveEventTab('completed')} style={{ background: 'none', border: 'none', padding: '10px 12px', fontSize: '14px', fontWeight: activeEventTab === 'completed' ? 600 : 500, color: activeEventTab === 'completed' ? 'var(--primary)' : 'var(--ink-soft)', cursor: 'pointer', borderBottom: activeEventTab === 'completed' ? '2px solid var(--primary)' : '2px solid transparent', marginBottom: '-1px', transition: 'all 0.2s' }}>Completed ({completed.length})</button>}
              {active.length > 0 && <button className={`etab ${activeEventTab === 'active' ? 'active' : ''}`} onClick={() => setActiveEventTab('active')} style={{ background: 'none', border: 'none', padding: '10px 12px', fontSize: '14px', fontWeight: activeEventTab === 'active' ? 600 : 500, color: activeEventTab === 'active' ? 'var(--primary)' : 'var(--ink-soft)', cursor: 'pointer', borderBottom: activeEventTab === 'active' ? '2px solid var(--primary)' : '2px solid transparent', marginBottom: '-1px', transition: 'all 0.2s' }}>Active ({active.length})</button>}
              {upcoming.length > 0 && <button className={`etab ${activeEventTab === 'upcoming' ? 'active' : ''}`} onClick={() => setActiveEventTab('upcoming')} style={{ background: 'none', border: 'none', padding: '10px 12px', fontSize: '14px', fontWeight: activeEventTab === 'upcoming' ? 600 : 500, color: activeEventTab === 'upcoming' ? 'var(--primary)' : 'var(--ink-soft)', cursor: 'pointer', borderBottom: activeEventTab === 'upcoming' ? '2px solid var(--primary)' : '2px solid transparent', marginBottom: '-1px', transition: 'all 0.2s' }}>Upcoming ({upcoming.length})</button>}
              {missed.length > 0 && <button className={`etab ${activeEventTab === 'missed' ? 'active' : ''}`} onClick={() => setActiveEventTab('missed')} style={{ background: 'none', border: 'none', padding: '10px 12px', fontSize: '14px', fontWeight: activeEventTab === 'missed' ? 600 : 500, color: activeEventTab === 'missed' ? 'var(--primary)' : 'var(--ink-soft)', cursor: 'pointer', borderBottom: activeEventTab === 'missed' ? '2px solid var(--primary)' : '2px solid transparent', marginBottom: '-1px', transition: 'all 0.2s' }}>Missed/Cancelled ({missed.length})</button>}
            </div>
          )}

          {activeEventTab === 'completed' && (
            completed.length > 0 ? (
              <div className="vtimeline">
                {completed.map((iv, idx) => renderEventCard(iv, idx, true))}
              </div>
            ) : (
              <div style={{ padding: '24px', textAlign: 'center', color: 'var(--ink-soft)' }}>No completed events</div>
            )
          )}

          {activeEventTab === 'active' && (
            active.length > 0 ? (
              <div className="vtimeline">
                {active.map((iv, idx) => renderEventCard(iv, idx, false))}
              </div>
            ) : (
              <div style={{ padding: '24px', textAlign: 'center', color: 'var(--ink-soft)' }}>No active events</div>
            )
          )}

          {activeEventTab === 'upcoming' && (
            upcoming.length > 0 ? (
              <div className="vtimeline">
                {upcoming.map((iv, idx) => renderEventCard(iv, idx, false))}
              </div>
            ) : (
              <div style={{ padding: '24px', textAlign: 'center', color: 'var(--ink-soft)' }}>No upcoming events</div>
            )
          )}

          {activeEventTab === 'missed' && (
            missed.length > 0 ? (
              <div className="vtimeline">
                {missed.map((iv, idx) => renderEventCard(iv, idx, false))}
              </div>
            ) : (
              <div style={{ padding: '24px', textAlign: 'center', color: 'var(--ink-soft)' }}>No missed or cancelled events</div>
            )
          )}
        </div>
      </>
    );
  };

  const renderJobsTab = () => (
    <>
      <button className="close-btn" onClick={handleClose}>✕ Close File</button>
      <div className="page-pad">
        <div className="page-head">
          <div><div className="eyebrow">Section 03 · Match Dashboard</div><h1>Job Search Results</h1></div>
          <div className="head-meta">Matches<br /><b style={{ color: 'var(--ink)' }}>{job_searches.length}</b></div>
        </div>
        <div className="job-grid">
          {job_searches.length === 0 && (
            <div className="empty-state empty-jobs">
              <EmptyJobIllustration />
              <h3>No Matches Found</h3>
              <p>There are currently no job matches for this candidate. Try running a new matching search to find opportunities.</p>
            </div>
          )}
          {job_searches.map((j: any, idx: number) => {
            const statusColor = j.status === "Shortlisted" ? "var(--ok)" : j.status === "In Review" ? "var(--warn)" : j.status === "New" ? "var(--tab-jobs)" : "var(--ink-faint)";
            return (
              <div className="job-card" key={idx} onClick={() => showJob(idx)}>
                <div className="job-top">
                  <div><div className="job-title">{j.title}</div><div className="job-company">Internal Match</div></div>
                  <MatchRing score={j.match_score || 0} showCenterText={true} />
                </div>
                <div className="score-bars">
                  <div className="score-bar-row"><span className="lbl">Skills</span><div className="score-bar-track"><div className="score-bar-fill" style={{ width: `${j.skills_score || 0}%` }}></div></div><span className="pct">{j.skills_score || 0}</span></div>
                  <div className="score-bar-row"><span className="lbl">Experience</span><div className="score-bar-track"><div className="score-bar-fill" style={{ width: `${j.experience_score || 0}%` }}></div></div><span className="pct">{j.experience_score || 0}</span></div>
                </div>
                <div className="job-meta-list" style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '12px', color: 'var(--ink-soft)', marginBottom: '12px', marginTop: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 600, color: 'var(--ink)' }}>Searched On</span>
                    <span>{j.search_date || 'N/A'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 600, color: 'var(--ink)' }}>Searched By</span>
                    <span>{j.searched_by || 'System'}</span>
                  </div>
                </div>
                <div className="job-status"><span className="dot" style={{ background: statusColor }}></span>{j.status || 'New'}</div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );

  const getSkillsList = (skills: any): string[] => {
    if (!skills) return [];
    if (Array.isArray(skills)) return skills.filter(Boolean);
    if (typeof skills === 'string') {
      return skills.split(',').map(s => s.trim()).filter(Boolean);
    }
    return [];
  };

  const renderJobDescriptionHtml = (desc: string) => {
    if (!desc) return null;
    const hasHtml = /<[a-z][\s\S]*>/i.test(desc);
    if (!hasHtml) {
      return <div className="opp-jd-html plain-text">{desc}</div>;
    }
    return <div className="opp-jd-html" dangerouslySetInnerHTML={{ __html: desc }} />;
  };

  const renderApplicationDetailsView = () => {
    const opp = timelineData?.opportunity || {};
    const fullApp = timelineData?.application || selectedApp;
    const history = timelineData?.timeline || [];
    const isRejected = Boolean(fullApp.rejected || (fullApp.stage || '').toLowerCase() === 'rejected');
    const isDeclined = Boolean(fullApp.declined || (fullApp.stage || '').toLowerCase() === 'declined');

    // 1. Base forward stages for this opportunity (excluding terminal exit stages)
    const rawOppStages = (opp.stages && opp.stages.length > 0)
      ? opp.stages
      : ["Applied", "Shortlisted", "Interviewed", "Offered", "Accepted"];

    const forwardStages: string[] = rawOppStages.filter(
      (s: string) => !["rejected", "declined"].includes(s.toLowerCase())
    );

    // 2. Build tailored progression path for this candidate
    interface StepperStage {
      name: string;
      status: 'completed' | 'active' | 'upcoming' | 'rejected' | 'declined';
    }

    let stepperStages: StepperStage[] = [];

    if (isRejected || isDeclined) {
      // Find the last forward stage reached before rejection or decline
      let lastForwardStage = '';

      // Check transition history for the stage transitioned from
      const terminalEntry = history.find((h: any) =>
        ['rejected', 'declined'].includes((h.stage || '').toLowerCase())
      );
      if (terminalEntry && terminalEntry.previous_stage) {
        lastForwardStage = terminalEntry.previous_stage;
      }

      // Fallback: look for the most recent non-terminal stage in history
      if (!lastForwardStage) {
        const lastActiveEntry = history.find((h: any) =>
          !['rejected', 'declined'].includes((h.stage || '').toLowerCase())
        );
        if (lastActiveEntry && lastActiveEntry.stage) {
          lastForwardStage = lastActiveEntry.stage;
        }
      }

      // Fallback: fullApp previous_stage
      if (!lastForwardStage && (fullApp as any)?.previous_stage) {
        lastForwardStage = (fullApp as any).previous_stage;
      }

      let lastIdx = forwardStages.findIndex(
        (s: string) => s.toLowerCase() === (lastForwardStage || '').toLowerCase()
      );

      // If lastForwardStage wasn't found in forwardStages, default to stage before Accepted, or Offered, or index 0
      if (lastIdx === -1) {
        const offeredIdx = forwardStages.findIndex((s: string) => s.toLowerCase().includes('offer'));
        if (offeredIdx !== -1) {
          lastIdx = offeredIdx;
        } else {
          lastIdx = Math.max(0, forwardStages.length - 2);
        }
      }

      // Include forward stages up to the stage from which candidate exited (all completed)
      const passedStages = forwardStages.slice(0, lastIdx + 1);
      stepperStages = passedStages.map((name: string) => ({
        name,
        status: 'completed' as const,
      }));

      // Append terminal node (Rejected or Declined)
      stepperStages.push({
        name: isRejected ? 'Rejected' : 'Declined',
        status: isRejected ? ('rejected' as const) : ('declined' as const),
      });
    } else {
      // Forward path for active or accepted candidates
      const currentStageStr = (fullApp.stage || 'Applied').toLowerCase();
      const currentIdx = forwardStages.findIndex((s: string) => s.toLowerCase() === currentStageStr);

      stepperStages = forwardStages.map((name: string, idx: number) => {
        let status: 'completed' | 'active' | 'upcoming' = 'upcoming';
        if (currentIdx > -1) {
          if (idx < currentIdx) {
            status = 'completed';
          } else if (idx === currentIdx) {
            status = currentStageStr === 'accepted' ? 'completed' : 'active';
          } else {
            status = 'upcoming';
          }
        } else {
          status = idx === 0 ? 'active' : 'upcoming';
        }
        return { name, status };
      });
    }
    const badgeType = getStageBadgeClass(fullApp.stage);
    const requiredSkills = getSkillsList(opp.skills_required);
    const preferredSkills = getSkillsList(opp.skills_preferred);

    return (
      <>
        <div className="top-actions">
          <button
            type="button"
            className="close-btn app-back-btn"
            onClick={() => {
              setSelectedApp(null);
              setTimelineData(null);
            }}
          >
            <ArrowLeft size={13} className="btn-icon" />
            <span>Back to Applications</span>
          </button>
          {opp.opportunity_id && (
            <button
              type="button"
              className="close-btn app-opportunity-btn"
              onClick={() => navigate(`/dashboard/job-applications/${opp.opportunity_id}`)}
            >
              <ExternalLink size={12} className="btn-icon" />
              <span>Open Opportunity</span>
            </button>
          )}
          <button className="close-btn" onClick={handleClose}>✕ Close File</button>
        </div>

        <div className="page-pad app-details-page-pad">
          {/* Breadcrumb Eyebrow */}
          <div className="app-detail-breadcrumb">
            <span className="crumb-link" onClick={() => { setSelectedApp(null); setTimelineData(null); }}>
              Section 04 · Applications Pipeline
            </span>
            <ChevronRight size={12} className="crumb-sep" />
            <span className="crumb-current">Application Dossier</span>
          </div>

          {/* Hero Card */}
          <div className={`app-detail-hero-card stage-${badgeType}`}>
            <div className="hero-main">
              <div className="hero-avatar">
                <Building2 size={24} />
              </div>
              <div className="hero-info">
                <div className="hero-org-row">
                  <span className="hero-org">{opp.for_organization_name || selectedApp.organization_name || 'Direct Job'}</span>
                  {(opp.department || selectedApp.department) && (
                    <span className="hero-dept">· {opp.department || selectedApp.department}</span>
                  )}
                </div>
                <h1 className="hero-title">{opp.title || selectedApp.opportunity_title || 'Job Opportunity'}</h1>
                <div className="hero-chips">
                  {(opp.work_location || selectedApp.work_location) && (
                    <span className="hero-chip">
                      <MapPin size={12} />
                      <span>{opp.work_location || selectedApp.work_location}</span>
                    </span>
                  )}
                  {(opp.job_type || selectedApp.employment_type || selectedApp.job_type) && (
                    <span className="hero-chip">
                      <Briefcase size={12} />
                      <span>{opp.job_type || selectedApp.employment_type || selectedApp.job_type}</span>
                    </span>
                  )}
                  {(opp.work_type || selectedApp.work_type) && (
                    <span className="hero-chip">
                      <Target size={12} />
                      <span>{opp.work_type || selectedApp.work_type}</span>
                    </span>
                  )}
                  {fullApp.created_at && (
                    <span className="hero-chip">
                      <Calendar size={12} />
                      <span>Applied {fullApp.created_at.split(',')[0]}</span>
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="hero-status-zone">
              <span className={`job-status-pill status-${badgeType} hero-pill`}>
                <span className="status-dot" />
                <span>{fullApp.stage || 'Applied'}</span>
              </span>

              {fullApp.match_score !== undefined && fullApp.match_score !== null && (
                <div className="hero-match-indicator">
                  <MatchRing score={fullApp.match_score} size={48} />
                  <div className="hero-match-meta">
                    <span className="match-num">{fullApp.match_score}%</span>
                    <span className="match-lbl">Match Score</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Rejection / Decline Notice Banner */}
          {(isRejected || isDeclined) && (
            <div className="app-decline-banner">
              <div className="decline-title">
                <AlertCircle size={16} />
                <span>Application {isRejected ? 'Rejected' : 'Declined'}</span>
              </div>
              {fullApp.rejection_reason_type && (
                <div className="decline-item">
                  <strong>Reason:</strong> {fullApp.rejection_reason_type.replace(/_/g, ' ')}
                </div>
              )}
              {fullApp.rejection_reason_text && (
                <div className="decline-item">
                  <strong>Explanation:</strong> {fullApp.rejection_reason_text}
                </div>
              )}
              {fullApp.rejected_at && (
                <div className="decline-time">Recorded at: {fullApp.rejected_at}</div>
              )}
            </div>
          )}

          {/* Detail Tabs Bar: Application Info vs Job Info */}
          <div className="app-detail-tabs-bar">
            <button
              type="button"
              className={`app-detail-tab ${detailViewTab === 'application' ? 'active' : ''}`}
              onClick={() => setDetailViewTab('application')}
            >
              {detailViewTab === 'application' && (
                <motion.div
                  layoutId="detailTabActiveIndicator"
                  className="tab-active-indicator"
                  transition={{ type: 'spring', stiffness: 450, damping: 35 }}
                />
              )}
              <UserCheck size={16} className="tab-icon" />
              <span className="tab-label">Application Info</span>
            </button>

            <button
              type="button"
              className={`app-detail-tab ${detailViewTab === 'job' ? 'active' : ''}`}
              onClick={() => setDetailViewTab('job')}
            >
              {detailViewTab === 'job' && (
                <motion.div
                  layoutId="detailTabActiveIndicator"
                  className="tab-active-indicator"
                  transition={{ type: 'spring', stiffness: 450, damping: 35 }}
                />
              )}
              <Briefcase size={16} className="tab-icon" />
              <span className="tab-label">Job Info</span>
            </button>
          </div>

          {timelineLoading ? (
            <div style={{ padding: '60px 0', textAlign: 'center' }}>
              <Loader isLoading={true} message="Loading Application Dossier..." />
            </div>
          ) : detailViewTab === 'application' ? (
            <>
              {/* Pipeline Stepper Card */}
              <div className="app-pipeline-card">
                <div className="pipeline-card-head">
                  <span className="pipeline-title">Stage Progression</span>
                  <span className="pipeline-sub">Current: <strong>{fullApp.stage || 'Applied'}</strong></span>
                </div>
                <div className="pipeline-stepper">
                  {stepperStages.map((stgObj, idx: number) => {
                    const isPassed = stgObj.status === 'completed';
                    const isCurrent = stgObj.status === 'active';
                    const isTerminal = stgObj.status === 'rejected' || stgObj.status === 'declined';
                    const isRejectedNode = stgObj.status === 'rejected';
                    const nextIsTerminal = idx < stepperStages.length - 1 &&
                      (stepperStages[idx + 1].status === 'rejected' || stepperStages[idx + 1].status === 'declined');
                    const nextIsRejected = idx < stepperStages.length - 1 && stepperStages[idx + 1].status === 'rejected';

                    const nodeClass = stgObj.status;

                    return (
                      <div key={idx} className={`pipeline-step-node ${nodeClass}`}>
                        {idx < stepperStages.length - 1 && (
                          <div
                            className={`pipeline-connector-line ${
                              nextIsTerminal
                                ? nextIsRejected
                                  ? 'terminal-connector-rejected'
                                  : 'terminal-connector-declined'
                                : ''
                            }`}
                          />
                        )}
                        <div className="pipeline-step-icon">
                          {isPassed ? '✓' : isTerminal ? '✕' : isCurrent ? idx + 1 : idx + 1}
                        </div>
                        <div className="pipeline-step-label">{stgObj.name}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* 2-Column Content Layout for Application Info */}
              <div className="app-detail-columns">
                {/* Left Column: Stage Activity & History */}
                <div className="app-detail-col-main">
                  <div className="app-section-card">
                    <div className="section-card-head">
                      <Activity size={16} className="head-icon" />
                      <h3>Stage Activity & History</h3>
                      <span className="badge-count">{history.length}</span>
                    </div>

                    {history.length === 0 ? (
                      <div className="empty-sub-card">
                        <p>No stage transitions recorded yet. The application is in its initial stage.</p>
                      </div>
                    ) : (
                      <div className="app-timeline-flow">
                        {history.map((h: any, i: number) => {
                          const badge = getStageBadgeClass(h.stage);
                          const isNoteOpen = expandedNotes.has(i);

                          return (
                            <div key={i} className={`app-timeline-event status-${badge}`}>
                              <div className="event-marker">
                                <span className={`marker-dot status-${badge}`} />
                                {i < history.length - 1 && <div className="event-line" />}
                              </div>
                              <div className="event-content">
                                <div className="event-head">
                                  <div className="event-head-left">
                                    <span className={`job-status-pill status-${badge} pill-sm`}>
                                      <span className="status-dot" />
                                      <span>{h.stage}</span>
                                    </span>
                                    {h.previous_stage ? (
                                      <div className="stage-transition-breadcrumb">
                                        <span className="stage-crumb from">{h.previous_stage}</span>
                                        <ArrowRight size={11} className="transition-arrow" />
                                        <span className="stage-crumb to">{h.stage}</span>
                                      </div>
                                    ) : (
                                      <span className="stage-initial-label">Initial Stage</span>
                                    )}
                                  </div>
                                  <div className="event-timestamp">
                                    <Clock size={11} />
                                    <span>{h.changed_at}</span>
                                  </div>
                                </div>

                                <div className="event-meta-row">
                                  {h.changed_by && (
                                    <div className="event-meta-chip author-chip" title={`Changed by ${h.changed_by}`}>
                                      <User size={11} />
                                      <span>{h.changed_by}</span>
                                    </div>
                                  )}

                                  {h.duration_in_previous_stage && (
                                    <div className="event-meta-chip duration-chip">
                                      <Clock size={11} />
                                      <span>
                                        Time in <strong>{h.previous_stage || h.stage}</strong>: {h.duration_in_previous_stage}
                                      </span>
                                    </div>
                                  )}

                                  {h.reason_type && (
                                    <div className="event-meta-chip reason-chip">
                                      <span>Reason: {h.reason_type.replace(/_/g, ' ')} {h.reason_text ? `(${h.reason_text})` : ''}</span>
                                    </div>
                                  )}

                                  {h.notes && (
                                    <button
                                      type="button"
                                      className={`event-note-btn ${isNoteOpen ? 'active' : ''}`}
                                      onClick={() => toggleNote(i)}
                                      title={isNoteOpen ? "Hide Note" : "View Note"}
                                    >
                                      <MessageSquare size={12} />
                                      <span>{isNoteOpen ? 'Hide Note' : 'View Note'}</span>
                                    </button>
                                  )}
                                </div>

                                <AnimatePresence>
                                  {isNoteOpen && h.notes && (
                                    <motion.div
                                      initial={{ opacity: 0, height: 0, marginTop: 0 }}
                                      animate={{ opacity: 1, height: 'auto', marginTop: 10 }}
                                      exit={{ opacity: 0, height: 0, marginTop: 0 }}
                                      transition={{ duration: 0.18 }}
                                      className="event-note-wrapper"
                                    >
                                      <div className="event-note-box">
                                        <div className="note-box-header">
                                          <MessageSquare size={11} className="note-icon" />
                                          <span>Stage Transition Note</span>
                                        </div>
                                        <p className="note-text">{h.notes}</p>
                                      </div>
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Right Column: Match Breakdown, Resume, Application Record */}
                <div className="app-detail-col-side">
                  {/* Match Evaluation Breakdown (if available) */}
                  {fullApp.match_breakdown && (
                    <div className="app-section-card side-card">
                      <div className="section-card-head">
                        <div className="side-head-icon-box icon-purple">
                          <Sparkles size={16} />
                        </div>
                        <h3>Match Breakdown</h3>
                      </div>

                      <div className="match-scores-list">
                        {fullApp.match_breakdown.skills_score !== undefined && (
                          <div className="score-row">
                            <span className="score-lbl">Skills</span>
                            <div className="score-track"><div className="score-bar" style={{ width: `${fullApp.match_breakdown.skills_score}%` }} /></div>
                            <span className="score-val">{fullApp.match_breakdown.skills_score}%</span>
                          </div>
                        )}
                        {fullApp.match_breakdown.experience_score !== undefined && (
                          <div className="score-row">
                            <span className="score-lbl">Experience</span>
                            <div className="score-track"><div className="score-bar" style={{ width: `${fullApp.match_breakdown.experience_score}%` }} /></div>
                            <span className="score-val">{fullApp.match_breakdown.experience_score}%</span>
                          </div>
                        )}
                        {fullApp.match_breakdown.education_score !== undefined && (
                          <div className="score-row">
                            <span className="score-lbl">Education</span>
                            <div className="score-track"><div className="score-bar" style={{ width: `${fullApp.match_breakdown.education_score}%` }} /></div>
                            <span className="score-val">{fullApp.match_breakdown.education_score}%</span>
                          </div>
                        )}
                      </div>

                      {fullApp.match_breakdown.reason && (
                        <div className="match-summary-quote">
                          <p>{fullApp.match_breakdown.reason}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Application Information Table */}
                  <div className="app-section-card side-card">
                    <div className="section-card-head">
                      <h3>Application Record</h3>
                    </div>
                    <div className="app-meta-list">
                      <div className="app-meta-item">
                        <span className="meta-key">Candidate</span>
                        <span className="meta-val meta-val-candidate">{fullName}</span>
                      </div>
                      {profile.email_address && (
                        <div className="app-meta-item">
                          <span className="meta-key">Email</span>
                          <span className="meta-val meta-val-email">{profile.email_address}</span>
                        </div>
                      )}
                      {fullApp.source && (
                        <div className="app-meta-item">
                          <span className="meta-key">Source</span>
                          <span className="meta-val">
                            <span className="source-pill">{fullApp.source}</span>
                          </span>
                        </div>
                      )}
                      {fullApp.created_at && (
                        <div className="app-meta-item">
                          <span className="meta-key">Created</span>
                          <span className="meta-val meta-val-date">{fullApp.created_at.split(',')[0]}</span>
                        </div>
                      )}
                      {fullApp.updated_at && (
                        <div className="app-meta-item">
                          <span className="meta-key">Last Updated</span>
                          <span className="meta-val meta-val-date">{fullApp.updated_at.split(',')[0]}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            /* detailViewTab === 'job' */
            <div className="app-detail-columns">
              {/* Left Column: Job Description */}
              <div className="app-detail-col-main">
                <div className="app-section-card opp-scope-card">
                  <div className="opp-scope-card-header">
                    <div className="opp-scope-title-wrap">
                      <div className="opp-scope-icon-box">
                        <FileText size={18} />
                      </div>
                      <div>
                        <h3 className="opp-scope-title">Opportunity Scope & Requirements</h3>
                        <p className="opp-scope-subtitle">Job specification and expectations for this requisition</p>
                      </div>
                    </div>
                  </div>

                  {opp.description ? (
                    <div className="opp-jd-container">
                      {renderJobDescriptionHtml(opp.description)}
                    </div>
                  ) : (
                    <div className="empty-sub-card">
                      <p>No job description details provided for this opportunity.</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column: Skills & Job Overview */}
              <div className="app-detail-col-side">
                {/* Dedicated Skills Container adjacent to the JD */}
                {(requiredSkills.length > 0 || preferredSkills.length > 0) && (
                  <div className="app-section-card side-card opp-skills-sidebar-card">
                    <div className="section-card-head">
                      <Sparkles size={16} className="head-icon" />
                      <h3>Skill Requirements</h3>
                    </div>

                    <div className="opp-skills-vertical-stack">
                      {/* Required Skills Card */}
                      {requiredSkills.length > 0 ? (
                        <div className="skills-spec-card required-card">
                          <div className="skills-spec-head">
                            <span className="skills-spec-square" />
                            <h4>REQUIRED SKILLS</h4>
                          </div>
                          <ul className="skills-spec-list">
                            {requiredSkills.map((s: string, idx: number) => (
                              <li key={idx}>{s}</li>
                            ))}
                          </ul>
                        </div>
                      ) : null}

                      {/* Preferred Skills Card */}
                      {preferredSkills.length > 0 ? (
                        <div className="skills-spec-card preferred-card">
                          <div className="skills-spec-head">
                            <Star size={14} className="star-icon" fill="#f59e0b" color="#f59e0b" />
                            <h4>PREFERRED SKILLS</h4>
                          </div>
                          <ul className="skills-spec-list">
                            {preferredSkills.map((s: string, idx: number) => (
                              <li key={idx}>{s}</li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                    </div>
                  </div>
                )}

                {/* Job Specifications & Role Meta */}
                <div className="app-section-card side-card">
                  <div className="section-card-head">
                    <h3>Job Overview</h3>
                  </div>
                  <div className="app-meta-list">
                    {(opp.for_organization_name || selectedApp.organization_name) && (
                      <div className="app-meta-item">
                        <span className="meta-key">Organization</span>
                        <span className="meta-val meta-val-org">{opp.for_organization_name || selectedApp.organization_name}</span>
                      </div>
                    )}
                    {(opp.department || selectedApp.department) && (
                      <div className="app-meta-item">
                        <span className="meta-key">Department</span>
                        <span className="meta-val">
                          <span className="dept-pill">{opp.department || selectedApp.department}</span>
                        </span>
                      </div>
                    )}
                    {(opp.work_location || selectedApp.work_location) && (
                      <div className="app-meta-item">
                        <span className="meta-key">Location</span>
                        <span className="meta-val meta-val-loc">{opp.work_location || selectedApp.work_location}</span>
                      </div>
                    )}
                    {(opp.job_type || selectedApp.employment_type || selectedApp.job_type) && (
                      <div className="app-meta-item">
                        <span className="meta-key">Employment</span>
                        <span className="meta-val">
                          <span className="type-pill">{opp.job_type || selectedApp.employment_type || selectedApp.job_type}</span>
                        </span>
                      </div>
                    )}
                    {(opp.work_type || selectedApp.work_type) && (
                      <div className="app-meta-item">
                        <span className="meta-key">Work Mode</span>
                        <span className="meta-val">
                          <span className="mode-pill">{opp.work_type || selectedApp.work_type}</span>
                        </span>
                      </div>
                    )}
                    {(opp.experience_years || opp.experience_level || opp.min_experience) && (
                      <div className="app-meta-item">
                        <span className="meta-key">Experience</span>
                        <span className="meta-val meta-val-exp">{opp.experience_years || opp.experience_level || opp.min_experience}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </>
    );
  };

  const renderJobApplicationsTab = () => {
    if (selectedApp) {
      return renderApplicationDetailsView();
    }
    const rawApps = candidateApplications.length > 0 ? candidateApplications : (data?.job_applications || []);
    const totalApps = rawApps.length;

    // Filter by search & stage tab
    const filteredApps = rawApps.filter((a: any) => {
      const q = (appSearch || '').toLowerCase().trim();
      const titleMatch = !q ||
        (a.opportunity_title || '').toLowerCase().includes(q) ||
        (a.organization_name || '').toLowerCase().includes(q) ||
        (a.department || '').toLowerCase().includes(q) ||
        (a.work_location || '').toLowerCase().includes(q) ||
        (a.employment_type || a.job_type || a.work_type || '').toLowerCase().includes(q);
      if (!titleMatch) return false;

      const s = (a.stage || '').toLowerCase();
      if (appFilter === 'active') return s !== 'rejected' && s !== 'declined' && s !== 'accepted' && s !== 'offered';
      if (appFilter === 'offered') return s === 'offered' || s === 'accepted';
      if (appFilter === 'rejected') return s === 'rejected' || s === 'declined';
      return true;
    });

    const activeCount = rawApps.filter((a: any) => {
      const s = (a.stage || '').toLowerCase();
      return s !== 'rejected' && s !== 'declined' && s !== 'accepted' && s !== 'offered';
    }).length;
    const offeredCount = rawApps.filter((a: any) => {
      const s = (a.stage || '').toLowerCase();
      return s === 'offered' || s === 'accepted';
    }).length;
    const rejectedCount = rawApps.filter((a: any) => {
      const s = (a.stage || '').toLowerCase();
      return s === 'rejected' || s === 'declined';
    }).length;

    // Sort applications
    const sortedApps = [...filteredApps].sort((a: any, b: any) => {
      if (appSort === 'newest') {
        const da = new Date(a.applied_at || a.created_at || 0).getTime();
        const db = new Date(b.applied_at || b.created_at || 0).getTime();
        return db - da;
      }
      if (appSort === 'oldest') {
        const da = new Date(a.applied_at || a.created_at || 0).getTime();
        const db = new Date(b.applied_at || b.created_at || 0).getTime();
        return da - db;
      }
      if (appSort === 'title') {
        return (a.opportunity_title || '').localeCompare(b.opportunity_title || '');
      }
      return 0;
    });

    return (
      <>
        <div className="top-actions">
          <button
            className="close-btn apply-btn"
            onClick={() => setIsApplyModalOpen(true)}
          >
            <Briefcase size={12} />
            <span>Apply to Job</span>
          </button>
          <button className="close-btn" onClick={handleClose}>✕ Close File</button>
        </div>
        <div className="page-pad">
          <div className="page-head">
            <div>
              <div className="eyebrow">Section 04 · Applications Pipeline</div>
              <h1>Job Applications</h1>
            </div>
          </div>

          {/* Dossier Stat Cards - Dashboard Frosted Glass UI */}
          <div className="app-dossier-summary-strip db-stats-row">
            <FrostedGlassStatCard
              icon={<Briefcase size={16} />}
              heading="Total Applications"
              count={totalApps}
              accent="purple"
              onClick={() => setAppFilter('all')}
              isActive={appFilter === 'all'}
            />
            <FrostedGlassStatCard
              icon={<Clock size={16} />}
              heading="In Review / Active"
              count={activeCount}
              accent="orange"
              onClick={() => setAppFilter(appFilter === 'active' ? 'all' : 'active')}
              isActive={appFilter === 'active'}
            />
            <FrostedGlassStatCard
              icon={<Trophy size={16} />}
              heading="Offered / Accepted"
              count={offeredCount}
              accent="green"
              onClick={() => setAppFilter(appFilter === 'offered' ? 'all' : 'offered')}
              isActive={appFilter === 'offered'}
            />
            <FrostedGlassStatCard
              icon={<AlertCircle size={16} />}
              heading="Rejected / Declined"
              count={rejectedCount}
              accent="pink"
              onClick={() => setAppFilter(appFilter === 'rejected' ? 'all' : 'rejected')}
              isActive={appFilter === 'rejected'}
            />
          </div>

          {/* Controls Bar: Below Stat Cards & Above Job Cards on the right end */}
          <div className="app-sub-toolbar">
            {appFilter !== 'all' ? (
              <div className="app-active-filter-strip">
                <span className="filter-pill-label">
                  <span className={`filter-indicator-dot dot-${appFilter}`} />
                  Filtered: <strong>{appFilter === 'active' ? 'In Review / Active' : appFilter === 'offered' ? 'Offered / Accepted' : 'Rejected / Declined'}</strong>
                  <span className="filter-count">({sortedApps.length})</span>
                </span>
                <button type="button" className="clear-filter-btn" onClick={() => setAppFilter('all')}>
                  <span>Show All</span>
                  <X size={11} />
                </button>
              </div>
            ) : <div />}

            <div className="app-head-controls">
              <div className="app-search-input-wrap">
                <Search size={14} className="search-icon" />
                <input
                  type="text"
                  placeholder="Search applications..."
                  value={appSearch}
                  onChange={(e) => setAppSearch(e.target.value)}
                />
                {appSearch && (
                  <X size={13} className="clear-icon" onClick={() => setAppSearch('')} />
                )}
              </div>

              <div className="app-sort-wrap">
                <select
                  className="app-sort-select"
                  value={appSort}
                  onChange={(e) => setAppSort(e.target.value as any)}
                >
                  <option value="newest">Newest Applied</option>
                  <option value="oldest">Oldest Applied</option>
                  <option value="title">Job Title (A-Z)</option>
                </select>
                <ChevronDown size={14} className="sort-chevron" />
              </div>
            </div>
          </div>

          {sortedApps.length === 0 ? (
            <div className="empty-state empty-jobs">
              <EmptyJobIllustration />
              <h3>No Job Applications Found</h3>
              <p>{totalApps === 0 ? "This candidate hasn't applied to any jobs yet." : "No applications matched your search or filter."}</p>
              {totalApps === 0 && (
                <div style={{ marginTop: '18px' }}>
                  <button
                    className="app-btn-apply"
                    onClick={() => setIsApplyModalOpen(true)}
                  >
                    <Briefcase size={14} />
                    <span>Apply Candidate to Job</span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="app-grid">
              {sortedApps.map((a: any, idx: number) => {
                const badgeType = getStageBadgeClass(a.stage);

                return (
                  <div
                    key={idx}
                    className={`applicant-job-card stage-${badgeType}`}
                    onClick={() => showApplicationTimeline(a)}
                  >
                    <div className="job-card-top">
                      <div className="job-card-avatar-wrap">
                        <div className="job-card-avatar">
                          <Building2 size={18} />
                        </div>
                        <div className="job-card-headings">
                          <h3 className="job-card-title" title={a.opportunity_title}>{a.opportunity_title || 'Opportunity'}</h3>
                          <div className="job-card-sub">
                            <span className="job-card-org">{a.organization_name || 'Direct Job'}</span>
                            {a.department && <span className="job-card-dept">· {a.department}</span>}
                          </div>
                        </div>
                      </div>

                      <span className={`job-status-pill status-${badgeType}`}>
                        <span className="status-dot" />
                        <span>{a.stage || 'Applied'}</span>
                      </span>
                    </div>

                    <div className="job-card-chips">
                      <div className="job-chip">
                        <Calendar size={12} />
                        <span>Applied {a.created_at ? a.created_at.split(',')[0] : 'Recently'}</span>
                      </div>
                      {a.work_location && (
                        <div className="job-chip">
                          <MapPin size={12} />
                          <span>{a.work_location}</span>
                        </div>
                      )}
                      {(a.employment_type || a.job_type || a.work_type) && (
                        <div className="job-chip">
                          <Briefcase size={12} />
                          <span>{a.employment_type || a.job_type || a.work_type}</span>
                        </div>
                      )}
                    </div>

                    <div className="job-card-divider" />

                    <div className="job-card-bottom">
                      <div className="job-card-score-area">
                        {a.match_score !== undefined && a.match_score !== null ? (
                          <div className="job-match-indicator">
                            <MatchRing score={a.match_score} size={32} />
                            <div className="job-match-text">
                              <span className="match-num">{a.match_score}%</span>
                              <span className="match-lbl">Match</span>
                            </div>
                          </div>
                        ) : (
                          <div className="job-eval-pill">
                            <span className="eval-dot" />
                            <span>In Pipeline</span>
                          </div>
                        )}
                      </div>

                      <button
                        type="button"
                        className="job-card-cta"
                        onClick={(e) => {
                          e.stopPropagation();
                          showApplicationTimeline(a);
                        }}
                      >
                        <span>View Details</span>
                        <ArrowRight size={13} className="cta-arrow" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </>
    );
  };

  const renderDocsTab = () => {
    const prof = data?.profile || {};
    const pass = prof.passport_details;
    const dl = prof.drivers_license_details;
    const visa = prof.visa_details;
    const aadhaar = prof.aadhaar_details;
    const pan = prof.pan_card_details;
    const ssn = prof.ssn_details;
    const bank = prof.bank_account_details;

    return (
      <>
        <button className="close-btn" onClick={handleClose}>✕ Close File</button>
        <div className="page-pad">
          <div className="page-head">
            <div><div className="eyebrow">Section 05 · Records</div><h1>Documents & Financials</h1></div>
            <div className="head-meta">Candidate<br /><b style={{ color: 'var(--ink)' }}>{fullName}</b></div>
          </div>

          <div className="section-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Identity Documents
            <button
              onClick={() => { setEditScope('docs'); setIsEditModalOpen(true); }}
              style={{ fontSize: '12px', color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
            >Edit</button>
          </div>
          <div className="doc-grid" style={{ marginBottom: '32px' }}>

            <div className={`doc-card ${!pass?.document_url && !pass?.passport_number ? 'missing' : ''}`}>
              <div className="dtype">Passport</div>
              <div className="dnum">{pass?.passport_number ? `P — ${pass.passport_number}` : 'Not on file'}</div>
              <div className="dextra">{pass?.expiry_date ? `Expires ${pass.expiry_date}` : '—'}</div>
              {pass?.document_url ? (
                <a href="#" className="dlink" onClick={(e) => { e.preventDefault(); showDocument('Passport', pass.document_url); }}>View scanned file</a>
              ) : (
                <span style={{ fontSize: '11px', color: 'var(--ink-faint)' }}>No file uploaded</span>
              )}
            </div>

            <div className={`doc-card ${!dl?.document_url && !dl?.license_number ? 'missing' : ''}`}>
              <div className="dtype">Driver's License</div>
              <div className="dnum">{dl?.license_number ? `DL — ${dl.license_number}` : 'Not on file'}</div>
              <div className="dextra">{dl?.expiry_date ? `Expires ${dl.expiry_date}` : '—'}</div>
              {dl?.document_url ? (
                <a href="#" className="dlink" onClick={(e) => { e.preventDefault(); showDocument("Driver's License", dl.document_url); }}>View scanned file</a>
              ) : (
                <span style={{ fontSize: '11px', color: 'var(--ink-faint)' }}>No file uploaded</span>
              )}
            </div>

            <div className={`doc-card ${!visa?.document_url && !visa?.visa_type ? 'missing' : ''}`}>
              <div className="dtype">Visa</div>
              <div className="dnum">{visa?.visa_type ? visa.visa_type : 'Not on file'}</div>
              <div className="dextra">{visa?.expiry_date ? `Expires ${visa.expiry_date}` : '—'}</div>
              {visa?.document_url ? (
                <a href="#" className="dlink" onClick={(e) => { e.preventDefault(); showDocument('Visa', visa.document_url); }}>View scanned file</a>
              ) : (
                <span style={{ fontSize: '11px', color: 'var(--ink-faint)' }}>No file uploaded</span>
              )}
            </div>

            <div className={`doc-card ${!aadhaar?.document_url && !aadhaar?.aadhaar_number ? 'missing' : ''}`}>
              <div className="dtype">Aadhaar</div>
              <div className="dnum">{aadhaar?.aadhaar_number || 'Not on file'}</div>
              <div className="dextra">{aadhaar?.aadhaar_number ? 'Verified' : '—'}</div>
              {aadhaar?.document_url ? (
                <a href="#" className="dlink" onClick={(e) => { e.preventDefault(); showDocument('Aadhaar', aadhaar.document_url); }}>View scanned file</a>
              ) : (
                <span style={{ fontSize: '11px', color: 'var(--ink-faint)' }}>No file uploaded</span>
              )}
            </div>

            <div className={`doc-card ${!pan?.document_url && !pan?.pan_number ? 'missing' : ''}`}>
              <div className="dtype">PAN</div>
              <div className="dnum">{pan?.pan_number || 'Not on file'}</div>
              <div className="dextra">{pan?.pan_number ? 'Verified' : '—'}</div>
              {pan?.document_url ? (
                <a href="#" className="dlink" onClick={(e) => { e.preventDefault(); showDocument('PAN', pan.document_url); }}>View scanned file</a>
              ) : (
                <span style={{ fontSize: '11px', color: 'var(--ink-faint)' }}>No file uploaded</span>
              )}
            </div>

            <div className={`doc-card ${!ssn?.document_url && !ssn?.ssn ? 'missing' : ''}`}>
              <div className="dtype">SSN</div>
              <div className="dnum">{ssn?.ssn || 'Not on file'}</div>
              <div className="dextra">—</div>
              {ssn?.document_url ? (
                <a href="#" className="dlink" onClick={(e) => { e.preventDefault(); showDocument('SSN', ssn.document_url); }}>View scanned file</a>
              ) : (
                <span style={{ fontSize: '11px', color: 'var(--ink-faint)' }}>No file uploaded</span>
              )}
            </div>

          </div>

          <div className="section-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Financial Details
            <button
              onClick={() => { setEditScope('financials'); setIsEditModalOpen(true); }}
              style={{ fontSize: '12px', color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
            >Edit</button>
          </div>
          <div style={{ background: 'var(--paper-2)', border: '1px solid var(--rule)', borderRadius: '12px', padding: '24px' }}>
            <div className="field-grid">
              <Field label="Bank Name" val={bank?.bank_name} />
              <Field label="Account Type" val={bank?.account_type} />
              <Field label="Account No." val={bank?.account_number} />
              <Field label="Routing No." val={bank?.routing_number} />
              <Field label="Branch" val={bank?.branch_name} />
              <Field label="IFSC Code" val={bank?.ifsc_code} />
            </div>
          </div>

        </div>
      </>
    );
  };

  return (
    <div className="portfolio-detail-wrapper" style={{ position: 'relative' }}>

      {/* ── Delete Modal ── */}
      <Modal
        open={deleteModal}
        onClose={() => setDeleteModal(false)}
        title="Delete Applicant"
        width="420px"
      >
        <p>Are you sure you want to delete this applicant? This action cannot be undone.</p>
        {data?.profile && (
          <p style={{ fontWeight: 600, marginTop: 8, color: "#0f172a" }}>
            {data.profile.first_name} {data.profile.last_name} ({data.profile.email_address})
          </p>
        )}
        <div className="modal-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '24px' }}>
          <button className="btn-cancel1" onClick={() => setDeleteModal(false)} style={{ padding: '8px 16px', border: '1px solid #cbd5e1', borderRadius: '4px', background: '#fff', cursor: 'pointer' }}>Cancel</button>
          <button className="btn-danger" onClick={handleDeleteConfirm} disabled={deleting} style={{ padding: '8px 16px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: '4px', cursor: deleting ? 'not-allowed' : 'pointer', opacity: deleting ? 0.6 : 1, fontWeight: 600 }}>
            {deleting ? "Deleting..." : "Delete"}
          </button>
        </div>
      </Modal>

      <div className="stage">
        <div className="dossier-wrap">
          <div className="folder-tabs">
            <button className={`ftab t-profile ${activeTab === 'profile' ? 'active' : ''}`} onClick={() => handleTabChange('profile')}>Profile</button>
            <button className={`ftab t-interviews ${activeTab === 'interviews' ? 'active' : ''}`} onClick={() => handleTabChange('interviews')}>Events</button>
            <button className={`ftab t-jobs ${activeTab === 'jobs' ? 'active' : ''}`} onClick={() => handleTabChange('jobs')}>Job Matches</button>
            <button className={`ftab t-applications ${activeTab === 'applications' ? 'active' : ''}`} onClick={() => handleTabChange('applications')}>
              Job Applications {(candidateApplications.length || job_applications.length) > 0 ? `(${candidateApplications.length || job_applications.length})` : ''}
            </button>
            <button className={`ftab t-docs ${activeTab === 'docs' ? 'active' : ''}`} onClick={() => handleTabChange('docs')}>Documents</button>
          </div>
          <div className="dossier">
            <AnimatePresence mode="wait" custom={tabDirection}>
              <motion.div
                key={activeTab}
                className="dossier-page"
                custom={tabDirection}
                variants={{
                  enter: (dir: number) => ({ x: dir > 0 ? '6%' : '-6%', opacity: 0, scale: 0.97 }),
                  center: { x: 0, opacity: 1, scale: 1 },
                  exit: (dir: number) => ({ x: dir > 0 ? '-4%' : '4%', opacity: 0, scale: 0.98 }),
                }}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.32, ease: [0.4, 0, 0.2, 1] }}
              >
                {isTabLoading ? (
                  <div className="tab-shimmer" style={{ padding: '44px 46px', display: 'flex', flexDirection: 'column', gap: '24px', height: '100%' }}>
                    <div className="shimmer-line" style={{ width: '30%', height: 32, borderRadius: 8, marginBottom: 16 }} />
                    <div style={{ display: 'flex', gap: 24 }}>
                      <div className="shimmer-line" style={{ width: 80, height: 80, borderRadius: '50%' }} />
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 12, justifyContent: 'center' }}>
                        <div className="shimmer-line" style={{ width: '40%', height: 24, borderRadius: 6 }} />
                        <div className="shimmer-line" style={{ width: '60%', height: 16, borderRadius: 6 }} />
                      </div>
                    </div>
                    <div className="shimmer-line" style={{ width: '100%', height: 120, borderRadius: 12, marginTop: 20 }} />
                    <div className="shimmer-line" style={{ width: '100%', height: 120, borderRadius: 12 }} />
                  </div>
                ) : (
                  <div style={{ animation: 'fadeIn 0.25s ease' }}>
                    {activeTab === 'profile' && renderProfileTab()}
                    {activeTab === 'interviews' && renderEventsTab()}
                    {activeTab === 'jobs' && renderJobsTab()}
                    {activeTab === 'applications' && renderJobApplicationsTab()}
                    {activeTab === 'docs' && renderDocsTab()}
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      <div className={`portfolio-overlay ${(drawerHtml || drawerContent) ? 'open' : ''}`} onClick={closeDrawer}></div>
      <div className={`portfolio-drawer ${(drawerHtml || drawerContent) ? 'open' : ''}`}>
        {!drawerContent && <button className="portfolio-drawer-close" onClick={closeDrawer}>✕</button>}
        {drawerHtml && <div dangerouslySetInnerHTML={drawerHtml} />}
        {drawerContent && <div>{drawerContent}</div>}
      </div>

      {isApplyModalOpen && (
        <ApplyToJobModal
          isOpen={isApplyModalOpen}
          onClose={() => setIsApplyModalOpen(false)}
          candidate={profile}
          onSuccess={() => {
            setAlertInfo({ show: true, title: 'Success', message: 'Application submitted successfully!', type: 'success' });
            loadData();
          }}
        />
      )}

      <PortfolioEditModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSuccess={() => {
          setIsEditModalOpen(false);
          loadData();
        }}
        learnerId={profile.email_address}
        editScope={editScope}
      />

      {/* ✨ Resume PDF Viewer Modal ✨ */}
      {isPreviewModalOpen && ReactDOM.createPortal(
        <div
          style={{ position: 'fixed', inset: 0, top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15,23,42,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)', padding: '24px 80px' }}
          onClick={closePreview}
        >
          <div
            style={{ background: '#fff', borderRadius: '20px', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,0.2)', overflow: 'hidden' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', flexShrink: 0, borderRadius: '20px 20px 0 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: 36, height: 36, borderRadius: '8px', background: '#dcfce7', color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                </div>
                <div>
                  <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '15px' }}>{previewTitle}</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Scanned Document</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <ResumeZoomControls
                  zoom={resumeZoom}
                  onZoomChange={setResumeZoom}
                  onSearchClick={() => resumeViewerRef.current?.openSearch()}
                  onDownloadClick={async () => {
                    const downloadUrl = await getResumeDownloadUrl(resumePreviewUrl);
                    if (downloadUrl) {
                      const a = document.createElement("a");
                      a.href = downloadUrl;
                      a.download = "resume";
                      a.click();
                    }
                  }}
                />
                <button
                  onClick={closePreview}
                  style={{ background: '#f1f5f9', border: 'none', color: '#475569', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 500, padding: '8px 14px', borderRadius: '8px', transition: 'all 0.2s' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#e2e8f0'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = '#f1f5f9'; }}
                >
                  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg> Close
                </button>
              </div>
            </div>
            {/* Body */}
            <div style={{ flex: 1, position: 'relative' }}>
              <Loader isLoading={pdfLoading} message="Loading Document..." />
              {resumePreviewUrl ? (
                <ResumeViewer
                  ref={resumeViewerRef}
                  key={resumePreviewUrl}
                  url={resumePreviewUrl}
                  zoom={resumeZoom}
                  onZoomChange={setResumeZoom}
                  onLoaded={() => setPdfLoading(false)}
                />
              ) : (!pdfLoading && (
                <div style={{ position: 'absolute', inset: 0, background: '#f8fafc', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '20px' }}>
                  <span style={{ color: '#64748b', fontSize: '14px', fontWeight: 500 }}>No document file available for {previewTitle}</span>
                </div>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}

      {alertInfo && (
        <Alert
          show={alertInfo.show}
          title={alertInfo.title}
          message={alertInfo.message}
          type={alertInfo.type}
          onClose={() => setAlertInfo(null)}
          primaryButtonText="OK"
          onPrimaryClick={() => setAlertInfo(null)}
        />
      )}
    </div>
  );
};

const ApplyToJobModal = ({
  isOpen,
  onClose,
  candidate,
  onSuccess
}: {
  isOpen: boolean;
  onClose: () => void;
  candidate: any;
  onSuccess: () => void;
}) => {
  const [jobs, setJobs] = useState<JobOpportunity[]>([]);
  const [selectedJob, setSelectedJob] = useState<string>('');
  const source = candidate.source || 'Other';
  const [loading, setLoading] = useState(false);
  const [jobsLoading, setJobsLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      setJobsLoading(true);
      JobOpportunityClient.listOpportunities({ status: 'Open' }).then(res => {
        setJobs(res.records);
        const firstAvailable = res.records.find(j => !candidate.applied_jobs?.includes(j.opportunity_id));
        if (firstAvailable) setSelectedJob(firstAvailable.opportunity_id!);
      }).catch(console.error)
        .finally(() => setJobsLoading(false));
    }
  }, [isOpen, candidate.applied_jobs]);

  const handleApply = async () => {
    if (!selectedJob) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('candidate_id', candidate.candidate_id);
      formData.append('email_address', candidate.email_address);
      formData.append('first_name', candidate.first_name);
      formData.append('last_name', candidate.last_name || '');
      formData.append('source', source);
      if (candidate.resume_url) formData.append('resume_url', candidate.resume_url);

      await JobApplicationClient.addApplicant(selectedJob, formData);
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Failed to apply', err);
      // Wait, we need to pass alert failure back up or just alert here.
      // Let's use standard alert if it fails, or maybe just console error?
      // Since alert is native, let's keep it simple or remove it.
      alert('Failed to apply to job. See console for details.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={isOpen} onClose={onClose} title="Apply to Job">
      <Loader isLoading={jobsLoading} message="Loading Opportunities..." />
      <Loader isLoading={loading} message="Submitting Application..." />
      <div style={{ padding: '20px' }}>
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>Select Open Job</label>
          <Select
            options={jobs.map(j => ({
              value: j.opportunity_id || '',
              label: j.title,
              isApplied: candidate.applied_jobs?.includes(j.opportunity_id),
              isDisabled: candidate.applied_jobs?.includes(j.opportunity_id),
            }))}
            value={jobs.find(j => j.opportunity_id === selectedJob) ? { value: selectedJob, label: jobs.find(j => j.opportunity_id === selectedJob)!.title } : null}
            onChange={(val: any) => { if (!val?.isApplied) setSelectedJob(val?.value || ''); }}
            placeholder="Select a job to apply"
            noOptionsMessage={() => "No open jobs available"}
            menuPortalTarget={document.body}
            styles={{
              ...selectStyles,
              option: (base: any, state: any) => ({
                ...base,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                color: state.data.isApplied ? '#94a3b8' : '#0f172a',
                background: state.isSelected ? 'var(--primary)' : state.isFocused ? '#f1f5f9' : '#fff',
                cursor: state.data.isApplied ? 'not-allowed' : 'pointer',
                opacity: state.data.isApplied ? 0.7 : 1,
              }),
            }}
            formatOptionLabel={(opt: any) => (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', width: '100%' }}>
                <span style={{ fontWeight: 500, fontSize: '0.875rem' }}>{opt.label}</span>
                {opt.isApplied && (
                  <span style={{
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #d1fae5, #a7f3d0)',
                    color: '#065f46',
                    border: '1px solid #6ee7b7',
                    borderRadius: '999px',
                    padding: '2px 8px',
                    letterSpacing: '0.4px',
                    flexShrink: 0,
                  }}>
                    ✓ Applied
                  </span>
                )}
              </div>
            )}
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          <button
            onClick={onClose}
            style={{ padding: '10px 16px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer' }}
          >Cancel</button>
          <button
            onClick={handleApply}
            disabled={!selectedJob || loading}
            style={{ padding: '10px 16px', borderRadius: '6px', border: 'none', background: 'var(--primary)', color: '#fff', cursor: loading || !selectedJob ? 'not-allowed' : 'pointer', opacity: loading || !selectedJob ? 0.7 : 1 }}
          >
            {loading ? 'Applying...' : 'Apply'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default PortfolioDetailPage;

// ─────────────────────────────────────────────────────────────────────────────
// Audit Trail Panel
// ─────────────────────────────────────────────────────────────────────────────

const PREVIEW_COUNT = 2;

const CATEGORY_META: Record<string, { icon: React.ReactNode; cls: string; label: string }> = {
  profile: { icon: <User size={16} />, cls: 'audit-icon-profile', label: 'Profile' },
  interview: { icon: <Video size={16} />, cls: 'audit-icon-interview', label: 'Event' },
  assessment: { icon: <FileText size={16} />, cls: 'audit-icon-assessment', label: 'Assessment' },
  job_search: { icon: <Search size={16} />, cls: 'audit-icon-job_search', label: 'Talent Search' },
  job_application: { icon: <Briefcase size={16} />, cls: 'audit-icon-job_application', label: 'Application' },
};

function formatAuditTimestamp(ts: string): string {
  if (!ts) return '—';
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return ts;
    const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const time = d.toLocaleTimeString('en-US', { hour12: true, hour: '2-digit', minute: '2-digit', second: '2-digit' });
    return `${date} at ${time}`;
  } catch {
    return ts;
  }
}

const AuditEntryItem = ({ entry, isDrawer }: { entry: any; isDrawer: boolean }) => {
  const [expanded, setExpanded] = useState(false);
  const meta = CATEGORY_META[entry.category] || { icon: <Activity size={16} />, cls: 'audit-icon-profile', label: 'Event' };

  const hasDetail = !!entry.detail;
  const hasPills = !!(entry.old_stage && entry.new_stage) || entry.score != null || !!entry.verdict;
  const hasExtra = hasDetail || hasPills;

  return (
    <li className="audit-entry">
      <div className={`audit-entry-icon ${meta.cls}`} title={meta.label}>
        {meta.icon}
      </div>
      <div className="audit-entry-body">
        <div className={`audit-entry-card audit-card-bg-${entry.category || 'default'}`}>
          <div
            className="audit-entry-card-header"
            onClick={() => hasExtra && setExpanded(!expanded)}
            style={{ cursor: hasExtra ? 'pointer' : 'default' }}
          >
            <div className="audit-entry-card-title-group">
              <span className="audit-entry-card-action" title={entry.action}>{entry.action}</span>
            </div>
            {hasExtra && (
              <div className="audit-entry-card-toggle">
                {expanded ? <ChevronUp size={16} strokeWidth={2} /> : <ChevronDown size={16} strokeWidth={2} />}
              </div>
            )}
          </div>

          <AnimatePresence>
            {expanded && hasExtra && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                style={{ overflow: 'hidden' }}
              >
                {hasPills && (
                  <div style={{ padding: '5px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                    {entry.old_stage && entry.new_stage && (
                      <div className="audit-entry-stage-pills">
                        <span className={`audit-pill audit-pill-${entry.old_stage.toLowerCase().replace(/\s+/g, '_')}`}>{entry.old_stage}</span>
                        <span className="audit-pill-arrow">→</span>
                        <span className={`audit-pill audit-pill-${entry.new_stage.toLowerCase().replace(/\s+/g, '_')}`}>{entry.new_stage}</span>
                      </div>
                    )}

                    {entry.score != null && (
                      <span className="audit-pill audit-pill-score" style={{ background: '#e0f2fe', color: '#0369a1', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Activity size={12} strokeWidth={2.5} />
                        {typeof entry.score === 'string' && entry.score.toLowerCase().includes('score')
                          ? entry.score
                          : `Score: ${entry.score}`}
                      </span>
                    )}

                    {entry.verdict && (
                      <span className={`audit-pill audit-pill-${entry.verdict.toLowerCase().replace(/\s+/g, '_')}`}>
                        {entry.verdict}
                      </span>
                    )}
                  </div>
                )}
                {hasDetail && (
                  <div className="audit-entry-terminal-box">
                    <div className="terminal-content">
                      <span className="terminal-prefix">Detail:</span>
                      <span className="terminal-value">{entry.detail}</span>
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          <div className="audit-entry-card-footer">
            <div className="audit-entry-meta-group">
              <div className="audit-meta-item">
                <Calendar size={13} className="audit-meta-icon" />
                <span>{formatAuditTimestamp(entry.timestamp)}</span>
              </div>
              {entry.actor && (
                <>
                  <span className="audit-meta-dot">•</span>
                  <div className="audit-meta-item">
                    <User size={13} className="audit-meta-icon" />
                    <span>{entry.actor}</span>
                  </div>
                </>
              )}
            </div>
            {/* <span className="audit-role-pill">Recruiter</span> */}
          </div>
        </div>
      </div>
    </li>
  );
};

const AuditTrailPanel = ({
  entries,
  onShowAll,
  isDrawer = false,
}: {
  entries: any[];
  onShowAll?: () => void;
  isDrawer?: boolean;
}) => {
  const visible = isDrawer ? entries : entries.slice(0, PREVIEW_COUNT);
  const hasMore = !isDrawer && entries.length > PREVIEW_COUNT;

  return (
    <div className={`audit-trail-panel ${isDrawer ? 'audit-trail-drawer-view' : 'audit-trail-card-view'}`}>

      {entries.length === 0 ? (
        <div className="audit-empty">
          <div className="audit-empty-icon">📋</div>
          <span className="audit-empty-text">No audit events recorded yet</span>
        </div>
      ) : (
        <>
          <ul className="audit-entries-list">
            {visible.map((entry: any, i: number) => (
              <AuditEntryItem key={entry.audit_id || i} entry={entry} isDrawer={isDrawer} />
            ))}
          </ul>

          {hasMore && onShowAll && (
            <button className="audit-show-more" onClick={onShowAll}>
              ▼ Show all {entries.length} events
            </button>
          )}
        </>
      )}
    </div>
  );
};
